import React, { useEffect, useState, useMemo, useContext } from "react";
import { useParams } from "react-router-dom";

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Button,
  Stack,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { useTheme, alpha } from "@mui/material/styles";
import DownloadForOfflineOutlinedIcon from "@mui/icons-material/DownloadForOfflineOutlined";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import { jwtDecode } from "jwt-decode";
import * as XLSX from "xlsx";

import { useTranslation } from "react-i18next";

import { SidebarContext } from "../Sidebar/SidebarProvider";
import PygResultadoFinalCharts from "./PygResultadoFinalCharts";
import PygEpigrafesRealPptoChart from "./PygEpigrafesRealPptoChart";
import { toRealPptoSerie } from "./pygRealPptoUtils";
import PygPipelineSerieDebugRow from "./PygPipelineSerieDebugRow";
import PygSerieDetalleDialog from "../PyGAnalitico/PygSerieDetalleDialog";
import { authFetch } from "../../api/authFetch";

const API_URL = process.env.REACT_APP_API_URL;

const meses = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

const cuentasVentas = [
  "70000000",
  "70000001",
  "70000004",
  "70000005",
  "70000006",
  "70000007",
  "70000008",
  "70800000",
];

const cuentasServicios = ["70500001", "70500002"];

const cuentasMercaderias = [
  "60000000", // COMPRAS DE MERCADERIAS
  "60000002", // COMPRA DE MUESTRAS
  "60200001", // COMPRA ACCESORIOS PRODUCTO
  "60200005", // COMPRA ETIQUETAS
  "60300000", // ARANCELES
  "60300001", // ARANCELES IMPORTACION MUESTRAS
  "60600000", // DESCUENTOS PRONTO PAGO
  "60800000", // DEVOLUCIONES DE COMPRAS
];

const cuentasMateriasPrimas = [
  "60100000", // COMPRAS DE MATERIAS PRIMAS
];

const cuentasTrabajosExternos = [
  "60700001", // GASTOS ADUANAS
  "60700002", // FLETES AEREOS
  "60700003", // FLETES MARITIMOS
  "60700004", // TRANSPORTE TERRESTRE
  "60700005", // CONTROL DE CALIDAD
  "60700006", // PICKING & MANIPULACION LOGISTICA
];

const cuentasOtrosIngresos = [
  "75900000", // INGRESOS POR SERVICIOS DIVERSOS
  "75900001", // DIFERENCIAS POSITIVAS POR AJUSTES EN FACTURAS
];

const cuentasSueldos = [
  "64000000", // SUELDOS Y SALARIOS
  "64000001", // COMISIONES VENTAS LYDIA URALDE
  "64000003", // BONUS ANUAL BORJA DEL REY
  "64100000", // INDEMNIZACIONES
  "64010000", // GASTOS BECARIOS
];

const cuentasCargasSociales = [
  "64200000", // SEGURIDAD SOCIAL A CARGO DE LA EMPRESA
  "64200001", // SEGURIDAD SOCIAL A CARGO DE LA EMPRESA AUTÓNOMOS
  "64900000", // DIETAS MANUTENCION AGUSTINA MICH
  "64900001", // DIETAS MANUTENCION CHIARA SANTUCCI
  "64900002", // DIETAS MANUTENCION ANA CARRASCO
  "64900003", // DIETAS MANUTENCION LYDIA URALDE
];

/** Ep.6 c) — provisiones de personal (vacío si no hay cuentas 64x de provisiones). */
const cuentasProvisiones = [];

/** Ep.7 c) — pérdidas / provisiones por operaciones comerciales (NPGC A.07.3). */
const cuentasProvisionesComerciales = [
  "65000001", // PÉRDIDAS DE CRÉDITOS COMERCIALES INCOBRABLES
];

const titulosCuentaPorCodigo = {
  64000003: "BONUS ANUAL BORJA DEL REY",
  62300011: "ASESORIA COMERCIAL CHILE",
  65000001: "PÉRDIDAS DE CRÉDITOS COMERCIALES INCOBRABLES",
};

const cuentasServiciosExteriores = [
  "62000001", // COMPRA MUESTRAS
  "62100000", // ARRENDAMIENTOS Y CÁNONES
  "62100002", // RENTING VEHICULOS
  "62200001", // REPARACIÓN Y MANTENIMIENTO INSTALACIONES
  "62300000",
  "62300001",
  "62300002",
  "62300003",
  "62300004",
  "62300005",
  "62300006",
  "62300008",
  "62300009", // GASTO LEASING PDTE REGULARIZAR
  "62300011", // ASESORIA COMERCIAL CHILE
  "62300010",
  "62400000",
  "62400001",
  "62400002",
  "62500000",
  "62500001",
  "62500004",
  "62500005",
  "62500006",
  "62600000",
  "62600001",
  "62800001",
  "62800002",
  "62800003",
  "62800004",
  "62800005",
  "62900000",
  "62900001",
  "62900002",
  "62900003",
  "62900004",
  "62900005",
  "62900006",
  "62900007",
  "62900014",
  "62900015",
  "62900060",
  "62900061",
  "62900062",
  "62900063",
  "62900064",
  "62900065",
  "62900066",
];

const cuentasTributos = [
  "60300000", // ARANCELES
  "63100000", // OTROS TRIBUTOS
  "63400000",
];

const cuentasOtrosGastosGestion = [
  "65900001", // DIFERENCIAS NEGATIVAS POR AJUSTES EN FACTURAS
];

const cuentasAmortizacion = [
  "68100000", // AMORTIZACIÓN DEL INMOVILIZADO MATERIAL
];

const cuentasOtrosResultados = [
  "67800000", // GASTOS EXCEPCIONALES
  "77800000", // INGRESOS EXCEPCIONALES
];

const cuentasDeudasTerceros = [
  "66120000",
  "66130000",
  "66170000",
  "66180000",
  "66220000",
  "66230000",
  "66240000",
  "66420000",
  "66430000",
  "66520000",
  "66530000",
  "66560000",
  "66570000",
  "66900000",
  "66500000",
];

const cuentasDeudasGrupo = [
  "66100000",
  "66200000", // por ejemplo
];

const cuentasActualizacionProvisiones = [
  "66910000", // ejemplo
];

const cuentasDiferenciasCambio = [
  "66810000",
  "76810000",
  "66800000",
  "76800000",
];

const lineasResumen = {
  "A.1) RESULTADO DE EXPLOTACIÓN": [
    "1. Importe neto de la cifra de negocios.",
    "2. Variación de existencias de productos terminados y en curso de fabricación.",
    "3. Trabajos realizados por la empresa para su activo.",
    "-4. Aprovisionamientos.",
    "-5. Otros ingresos de explotación.",
    "-6. Gastos de personal.",
    "-7. Otros gastos de explotación.",
    "-8. Amortización del inmovilizado.",
    "9. Imputación de subvenciones de inmovilizado no financiero y otras.",
    "10. Excesos de provisiones.",
    "-11. Deterioro y resultado por enajenaciones del inmovilizado.",
    "12. Otros resultados.",
    "13. Diferencia negativa de combinaciones de negocio",
  ],

  "11. Deterioro y resultado por enajenaciones del inmovilizado.": [
    "a) Deterioros y pérdidas.",
    "b) Resultados por enajenaciones y otras.",
  ],

  "a) Deterioros y pérdidas.": ["67000000"],

  "b) Resultados por enajenaciones y otras.": ["77100000", "67100000"],

  "7. Otros gastos de explotación": [
    "a) Servicios exteriores",
    "b) Tributos",
    "c) Pérdidas, deterioro y variación de provisiones por operaciones comerciales",
    "d) Otros gastos de gestión corriente",
  ],

  // BLOQUE FINANCIERO
  "A.2) RESULTADO FINANCIERO": [
    "14. Ingresos financieros.",
    "-15. Gastos financieros",
    "16. Variación del valor razonable en instrumentos financieros.",
    "17. Diferencias de cambio.",
    "18. deterioro y resultado por enajenaciones de instrumentos financieros",
    "19. Otros ingresos y gastos de caracter financiero",
  ],

  "14. Ingresos financieros.": [
    "a) De participaciones en instrumentos de patrimonio.",
    "b) De valores negociables y de créditos del activo inmovilizado.",
    "c) Imputación de subvenciones, donaciones y legados de carácter financiero",
  ],

  "a) De participaciones en instrumentos de patrimonio.": [
    "a 1) En empresas del grupo y asociadas.",
    "a 2) En terceros.",
  ],

  "b) De valores negociables y de créditos del activo inmovilizado.": [
    "b 1) De empresas del grupo y asociadas.",
    "b 2) De terceros.",
  ],

  "15. Gastos financieros": [
    "a) Por deudas con empresas del grupo y asociadas.",
    "b) Por deudas con terceros.",
    "c) Por actualización de provisiones.",
  ],

  "16. Variación del valor razonable en instrumentos financieros.": [
    "a) Cartera de negociación y otros.",
    "b) Imputación al resultado por activos financieros disponibles para la venta",
  ],

  "18. Deterioro y resultado por enajenaciones de instrumentos financieros.": [
    "a) Deterioros y pérdidas.",
    "b) Resultados por enajenaciones y otras.",
  ],

  "19. Otros ingresos y gastos de carácter financiero": [
    "a) Incorporación al activo de gastos financieros",
    "b) Ingresos financieros derivados de convenios de acreedores.",
    "c) Resto de ingresos y gastos.",
  ],

  "B) RESULTADO FINANCIERO": ["A.2) RESULTADO FINANCIERO"],

  "C) RESULTADO ANTES DE IMPUESTOS": [
    "A.1) RESULTADO DE EXPLOTACIÓN",
    "B) RESULTADO FINANCIERO",
  ],

  "D) RESULTADO DEL EJERCICIO PROCEDENTE DE OPERACIONES CONTINUADAS": [
    "C) RESULTADO ANTES DE IMPUESTOS",
    "-20. Impuestos sobre beneficios",
  ],

  "E) RESULTADO DEL EJERCICIO": [
    "D) RESULTADO DEL EJERCICIO PROCEDENTE DE OPERACIONES CONTINUADAS",
  ],
};

const getNodosResumenManual = (accData) => [
  {
    codigo: "6. Gastos de personal.",
    data: sumarPorCuentasPorMes(
      [...cuentasSueldos, ...cuentasCargasSociales, ...cuentasProvisiones],
      accData,
    ),
  },
  {
    codigo: "7. Otros gastos de explotación",
    data: {
      ...cuentasServiciosExteriores,
      ...cuentasTributos,
      ...cuentasOtrosGastosGestion,
    }
      ? accData.reduce((acc, a) => {
          if (
            [
              ...cuentasServiciosExteriores,
              ...cuentasTributos,
              ...cuentasOtrosGastosGestion,
            ].includes(a.codigo)
          ) {
            acc[a.mes] = (acc[a.mes] || 0) + a.valor;
            acc.total = (acc.total || 0) + a.valor;
          }
          return acc;
        }, {})
      : {},
  },
  {
    codigo: "5. Otros ingresos de explotación.",
    data: sumarPorCuentasPorMes(cuentasOtrosIngresos, accData),
  },
  {
    codigo: "4. Aprovisionamientos.",
    data: {
      ...cuentasMercaderias,
      ...cuentasMateriasPrimas,
    }
      ? accData.reduce((acc, a) => {
          if (
            [...cuentasMercaderias, ...cuentasMateriasPrimas].includes(a.codigo)
          ) {
            acc[a.mes] = (acc[a.mes] || 0) + a.valor;
            acc.total = (acc.total || 0) + a.valor;
          }
          return acc;
        }, {})
      : {},
  },
  {
    codigo: "12. Otros resultados",
    data: sumarPorCuentasPorMes(cuentasOtrosResultados, accData),
  },
  {
    codigo: "17. Diferencias de cambio.",
    data: sumarPorCuentasPorMes(
      ["66800000", "66810000", "76800000", "76810000"],
      accData,
    ),
  },
];

const calcularResumen = (
  nombreLinea,
  estructura,
  accData,
  cacheResultados = {},
) => {
  if (cacheResultados[nombreLinea]) {
    return cacheResultados[nombreLinea];
  }
  const formula = lineasResumen[nombreLinea];
  if (!formula)
    return accData
      .filter((a) => a.codigo === nombreLinea || a.titulo === nombreLinea)
      .reduce((res, a) => {
        const mes = a.mes?.toString().padStart(2, "0");
        if (!res[mes]) res[mes] = 0;
        res[mes] += a.valor || 0;
        res.total = (res.total || 0) + (a.valor || 0);
        return res;
      }, {});
  const valores = {};
  let totalGeneral = 0;

  meses.forEach((mes) => {
    let totalMes = 0;

    formula.forEach((codigo) => {
      const signo = codigo.startsWith("-") ? -1 : 1;
      const cleanCodigo = codigo.replace("-", "").trim();

      if (lineasResumen[cleanCodigo]) {
        // Recursión con cache
        const resultadoHijo =
          cacheResultados[cleanCodigo] ||
          calcularResumen(cleanCodigo, estructura, accData, cacheResultados);

        cacheResultados[cleanCodigo] = resultadoHijo;
        totalMes += signo * (resultadoHijo[mes] || 0);
      } else {
        const normalizar = (str) =>
          (str || "")
            .toLowerCase()
            .replace(/\./g, "")
            .replace(/\s+/g, " ")
            .trim();

        const match =
          estructura.find(
            (e) =>
              normalizar(e.codigo) === normalizar(cleanCodigo) ||
              normalizar(e.titulo) === normalizar(cleanCodigo),
          ) ||
          getNodosResumenManual(accData).find(
            (n) => normalizar(n.codigo) === normalizar(cleanCodigo),
          );
        if (!match) return;
        if (match.data) {
          totalMes += signo * (match.data[mes] || 0);
        } else {
          const sumaMes = accData
            .filter((a) => a.codigo === match.codigo && a.mes === parseInt(mes))
            .reduce((acc, a) => acc + (a.valor || 0), 0);

          totalMes += signo * sumaMes;
        }
      }
    });

    valores[mes] = totalMes;
    totalGeneral += totalMes;
  });

  return { ...valores, total: totalGeneral };
};

const sumarPorCuentasPorMes = (cuentas, accData) => {
  const resultado = {};
  let totalAnual = 0;

  for (let mes = 1; mes <= 12; mes++) {
    const totalMes = cuentas.reduce((acc, codigo) => {
      const match = accData.find((a) => a.codigo === codigo && a.mes === mes);
      return acc + (match?.valor || 0);
    }, 0);

    resultado[mes] = totalMes;
    totalAnual += totalMes;
  }

  return { ...resultado, total: totalAnual };
};

const PygDebugTable = () => {
  const theme = useTheme();
  const gantBg = theme.palette.gant?.main ?? theme.palette.gant ?? "#e0e0e0";
  const navigate = useNavigate();
  const { isMinimized } = useContext(SidebarContext);

  const { i18n, t } = useTranslation();
  /* const marginLeft = isMinimized ? "2.5em" : "13.5em"; */

  const { empresa: empresaParam } = useParams();
  const empresa = (empresaParam || "fusion").toLowerCase();

  const tituloMap = {
    stage: "PyG · Stage",
    erna: "PyG · ERNA",
    fusion: "PyG · Fusionado",
  };

  const titulo = tituloMap[empresa] || "PyG";

  const EMPRESA_OPTIONS = [
    { value: "stage", label: "Stage" },
    { value: "erna", label: "ERNA" },
    { value: "fusion", label: "Fusión" },
  ];

  const onEmpresaChange = (nextEmpresa) => {
    const target =
      nextEmpresa === "fusion"
        ? "/pygdebug"
        : `/pygdebug/${encodeURIComponent(nextEmpresa)}`;
    navigate(target);
  };

  const { contentMargin } = useContext(SidebarContext);

  const [estructura, setEstructura] = useState([]);
  const [accData, setAccData] = useState([]);
  const [sumaVentas, setSumaVentas] = useState({});
  const [sumaServicios, setSumaServicios] = useState({});

  const [selectedYear, setSelectedYear] = useState(2026);

  const [estimateData, setEstimateData] = useState([]);

  const [proyeccionVentas, setProyeccionVentas] = useState([]);
  const [proyeccionCompras, setProyeccionCompras] = useState([]);
  const [pedidos_venta, setPedidos_venta] = useState([]);
  const [pedidos_compra, setPedidos_compra] = useState([]);
  const [facturas_venta_borrador, setFacturas_venta_borrador] = useState([]);
  const [detalleDialog, setDetalleDialog] = useState(null);

  const openSerieDetalle = (titulo, mes = null, totalEsperado = null) => {
    setDetalleDialog({ titulo, mes, totalEsperado });
  };

  const closeSerieDetalle = () => setDetalleDialog(null);

  /* expandir contraer grupos */
  const [gruposAbiertos, setGruposAbiertos] = useState({
    ventas: false,
    aprovisionamientos: false,
    prestaciones: false,
    variacionExistencias: false,
    trabajosActivo: false,
    otrosIngresosExplotacion: false,
    gastosPersonal: false,
    otrosGastosExplotacion: false,
    otrosGastosGestion: false,
    amortizacion: false,
    enajenaciones: false,
    otrosResultados: false,
    ingresosFinancieros: false,
    gastosFinancieros: false,
    valorRazonable: false,
    diferenciasCambio: false,
  });

  const toggleGrupo = (grupo) => {
    setGruposAbiertos((prev) => ({
      ...prev,
      [grupo]: !prev[grupo],
    }));
  };
  /* expandir contraer grupos */

  const getEstimateMeses = (cuentas) => {
    const resultado = {};
    let totalAnual = 0;

    for (let mes = 1; mes <= 12; mes++) {
      const totalMes = cuentas.reduce((acc, codigo) => {
        const match = estimateData.find(
          (e) => e.codigo === codigo && e.mes === mes,
        );
        return acc + (match?.valor || 0);
      }, 0);
      resultado[mes] = totalMes;
      totalAnual += totalMes;
    }

    return { ...resultado, total: totalAnual };
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
    } else {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          localStorage.removeItem("token");
          navigate("/login");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        navigate("/login");
      }
    }
  }, [navigate]);

  useEffect(() => {
    authFetch(`${API_URL}/api/pyg/estructura`)
      .then((res) => res.json())
      .then((data) => setEstructura(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("❌ Error cargando estructura PYG:", err);
        setEstructura([]);
      });
  }, []);

  useEffect(() => {
    authFetch(`${API_URL}/api/pyg/acc?year=${selectedYear}&empresa=${empresa}`)
      .then((res) => res.json())
      .then((data) => setAccData(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("❌ Error cargando accData PYG:", err);
        setAccData([]);
      });
  }, [selectedYear, empresa]);

  useEffect(() => {
    authFetch(
      `${API_URL}/api/pyg/estimate?year=${selectedYear}&empresa=${empresa}`,
    )
      .then((res) => res.json())
      .then((data) => setEstimateData(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("❌ Error cargando estimateData PYG:", err);
        setEstimateData([]);
      });
  }, [selectedYear, empresa]);

  useEffect(() => {
    authFetch(
      `${API_URL}/api/pyg/proyeccion_ventas?year=${selectedYear}&empresa=${empresa}`,
    )
      .then((res) => res.json())
      .then((data) => setProyeccionVentas(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("❌ Error cargando proyeccion ventas:", err);
        setProyeccionVentas([]);
      });
  }, [selectedYear, empresa]);

  useEffect(() => {
    authFetch(
      `${API_URL}/api/pyg/proyeccion_compras?year=${selectedYear}&empresa=${empresa}`,
    )
      .then((res) => res.json())
      .then((data) => setProyeccionCompras(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("❌ Error cargando proyeccion compras:", err);
        setProyeccionCompras([]);
      });
  }, [selectedYear, empresa]);

  useEffect(() => {
    authFetch(
      `${API_URL}/api/pyg/pedidos_venta?year=${selectedYear}&empresa=${empresa}`,
    )
      .then((res) => res.json())
      .then((data) => setPedidos_venta(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("❌ Error cargando pedidos venta:", err);
        setPedidos_venta([]);
      });
  }, [selectedYear, empresa]);

  useEffect(() => {
    authFetch(
      `${API_URL}/api/pyg/pedidos_compra?year=${selectedYear}&empresa=${empresa}`,
    )
      .then((res) => res.json())
      .then((data) => setPedidos_compra(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("❌ Error cargando pedidos compra:", err);
        setPedidos_compra([]);
      });
  }, [selectedYear, empresa]);

  useEffect(() => {
    authFetch(
      `${API_URL}/api/pyg/facturas_venta_borrador?year=${selectedYear}&empresa=${empresa}`,
    )
      .then((res) => res.json())
      .then((data) =>
        setFacturas_venta_borrador(Array.isArray(data) ? data : []),
      )
      .catch((err) => {
        console.error("❌ Error cargando facturas venta borrador:", err);
        setFacturas_venta_borrador([]);
      });
  }, [selectedYear, empresa]);

  // Suma total de a) Ventas.
  useEffect(() => {
    const sumas = {};
    let totalAnual = 0;

    for (let mes = 1; mes <= 12; mes++) {
      const registrosMes = accData.filter(
        (a) => cuentasVentas.includes(a.codigo) && a.mes === mes,
      );

      const sumaMes = registrosMes.reduce(
        (acc, a) => acc + (parseFloat(a.valor) || 0),
        0,
      );

      sumas[mes] = sumaMes;
      totalAnual += sumaMes;
    }

    setSumaVentas({ ...sumas, total: totalAnual });
  }, [estructura, accData]);

  // Suma total de b) Prestaciones de servicios.
  useEffect(() => {
    const sumas = {};
    let totalAnual = 0;

    for (let mes = 1; mes <= 12; mes++) {
      const registrosMes = accData.filter(
        (a) => cuentasServicios.includes(a.codigo) && a.mes === mes,
      );

      const sumaMes = registrosMes.reduce(
        (acc, a) => acc + (parseFloat(a.valor) || 0),
        0,
      );

      sumas[mes] = sumaMes;
      totalAnual += sumaMes;
    }

    setSumaServicios({ ...sumas, total: totalAnual });
  }, [estructura, accData]);

  // Calcular 4. Aprovisionamientos.
  const totalAprovisionamientos = {};
  let totalAnualAprovisionamientos = 0;

  meses.forEach((mes) => {
    const totalMes =
      cuentasMercaderias.filter(Boolean).reduce((acc, codigo) => {
        const mesData = accData.find(
          (a) => a.codigo === codigo && a.mes === parseInt(mes),
        );
        return acc + (mesData?.valor || 0);
      }, 0) +
      cuentasMateriasPrimas.filter(Boolean).reduce((acc, codigo) => {
        const mesData = accData.find(
          (a) => a.codigo === codigo && a.mes === parseInt(mes),
        );
        return acc + (mesData?.valor || 0);
      }, 0);

    totalAprovisionamientos[mes] = totalMes;
    totalAnualAprovisionamientos += totalMes;
  });

  // Calcular 5. Otros ingresos de explotación
  const totalOtrosIngresosExplotacion = {};
  let totalAnualOtrosIngresosExplotacion = 0;

  meses.forEach((mes) => {
    const totalMes =
      cuentasOtrosIngresos.filter(Boolean).reduce((acc, codigo) => {
        const mesData = accData.find(
          (a) => a.codigo === codigo && a.mes === parseInt(mes),
        );
        return acc + (mesData?.valor || 0);
      }, 0) + 0; // En el futuro puedes sumar aquí las subvenciones si se añaden códigos

    totalOtrosIngresosExplotacion[mes] = totalMes;
    totalAnualOtrosIngresosExplotacion += totalMes;
  });

  const resultadoGastosPersonal = useMemo(() => {
    return sumarPorCuentasPorMes(
      [...cuentasSueldos, ...cuentasCargasSociales, ...cuentasProvisiones],
      accData,
    );
  }, [accData]);

  const resultadoProvisionesComerciales = useMemo(() => {
    return sumarPorCuentasPorMes(cuentasProvisionesComerciales, accData);
  }, [accData]);

  /*   const resultadoExplotacion = useMemo(() => {
    return calcularResumen(
      "A.1) RESULTADO DE EXPLOTACIÓN",
      estructura,
      accData
    );
  }, [estructura, accData]); */

  const resultadoOtrosGastosExplotacion = useMemo(() => {
    const resultado = {};
    let totalAnual = 0;

    for (let mes = 1; mes <= 12; mes++) {
      const totalMes =
        cuentasServiciosExteriores.reduce((acc, codigo) => {
          const match = accData.find(
            (a) => a.codigo === codigo && a.mes === mes,
          );
          return acc + (match?.valor || 0);
        }, 0) +
        cuentasTributos.reduce((acc, codigo) => {
          const match = accData.find(
            (a) => a.codigo === codigo && a.mes === mes,
          );
          return acc + (match?.valor || 0);
        }, 0) +
        cuentasProvisionesComerciales.reduce((acc, codigo) => {
          const match = accData.find(
            (a) => a.codigo === codigo && a.mes === mes,
          );
          return acc + (match?.valor || 0);
        }, 0) +
        cuentasOtrosGastosGestion.reduce((acc, codigo) => {
          const match = accData.find(
            (a) => a.codigo === codigo && a.mes === mes,
          );
          return acc + (match?.valor || 0);
        }, 0);

      resultado[mes] = totalMes;
      totalAnual += totalMes;
    }

    return { ...resultado, total: totalAnual };
  }, [accData]);

  const resultadoA1Explotacion = useMemo(() => {
    const resultado = {};
    let totalAnual = 0;

    for (let mes = 1; mes <= 12; mes++) {
      const ventasMes = sumaVentas[mes] || 0;
      const serviciosMes = sumaServicios[mes] || 0;

      const variacionExistencias = accData
        .filter((a) => a.codigo === "71000000" && a.mes === mes)
        .reduce((sum, a) => sum + (a.valor || 0), 0);

      const trabajosEmpresaActivo = accData
        .filter((a) => a.codigo === "73000000" && a.mes === mes)
        .reduce((sum, a) => sum + (a.valor || 0), 0);

      const aprovisionamientos = totalAprovisionamientos[mes] || 0;
      const otrosIngresos = totalOtrosIngresosExplotacion[mes] || 0;
      const gastosPersonal = resultadoGastosPersonal[mes] || 0;
      const otrosGastos = resultadoOtrosGastosExplotacion[mes] || 0;

      const amortizacion = cuentasAmortizacion.reduce((acc, codigo) => {
        const match = accData.find((a) => a.codigo === codigo && a.mes === mes);
        return acc + (match?.valor || 0);
      }, 0);

      const imputacionSubvenciones = accData
        .filter((a) => a.codigo === "74600000" && a.mes === mes)
        .reduce((sum, a) => sum + (a.valor || 0), 0);

      const excesosProvisiones = accData
        .filter((a) => a.codigo === "79400000" && a.mes === mes)
        .reduce((sum, a) => sum + (a.valor || 0), 0);

      const resultadoInmovilizado = accData
        .filter(
          (a) =>
            ["67000000", "67100000", "77100000"].includes(a.codigo) &&
            a.mes === mes,
        )
        .reduce((sum, a) => sum + (a.valor || 0), 0);

      const diferenciaNegativa = 0;

      const otrosResultados = cuentasOtrosResultados.reduce((acc, codigo) => {
        const match = accData.find((a) => a.codigo === codigo && a.mes === mes);
        return acc + (match?.valor || 0);
      }, 0);

      const resultadoMes =
        ventasMes +
        serviciosMes +
        variacionExistencias +
        trabajosEmpresaActivo +
        aprovisionamientos +
        otrosIngresos +
        gastosPersonal +
        otrosGastos +
        amortizacion +
        imputacionSubvenciones +
        excesosProvisiones +
        resultadoInmovilizado +
        diferenciaNegativa +
        otrosResultados;

      // 🔍 Aquí vemos cada valor del mes
      if (mes === 1) {
        /* console.log("== DETALLE ENERO ==");
        console.log("ventasMes:", ventasMes);
        console.log("serviciosMes:", serviciosMes);
        console.log("variacionExistencias:", variacionExistencias);
        console.log("trabajosEmpresaActivo:", trabajosEmpresaActivo);
        console.log("aprovisionamientos:", aprovisionamientos);
        console.log("otrosIngresos:", otrosIngresos);
        console.log("gastosPersonal:", gastosPersonal);
        console.log("otrosGastos:", otrosGastos);
        console.log("amortizacion:", amortizacion);
        console.log("imputacionSubvenciones:", imputacionSubvenciones);
        console.log("excesosProvisiones:", excesosProvisiones);
        console.log("resultadoInmovilizado:", resultadoInmovilizado);
        console.log("otrosResultados:", otrosResultados);
        console.log("RESULTADO A.1:", resultadoMes); */
      }

      resultado[mes] = resultadoMes;
      totalAnual += resultadoMes;
    }

    resultado.total = totalAnual;
    return resultado;
  }, [
    accData,
    sumaVentas,
    sumaServicios,
    totalAprovisionamientos,
    totalOtrosIngresosExplotacion,
    resultadoGastosPersonal,
    resultadoOtrosGastosExplotacion,
  ]);

  const resultadoDeudasTerceros = useMemo(() => {
    return sumarPorCuentasPorMes(cuentasDeudasTerceros, accData);
  }, [accData]);

  const estimateVentas = useMemo(() => {
    return getEstimateMeses(cuentasVentas);
  }, [estimateData]);

  const estimateServicios = useMemo(() => {
    return getEstimateMeses(cuentasServicios);
  }, [estimateData]);

  const estimateVariacionExistencias = useMemo(() => {
    return getEstimateMeses(["71000000"]);
  }, [estimateData]);

  const cuentasAprovisionamientos = [
    ...cuentasMercaderias,
    ...cuentasMateriasPrimas,
  ];

  const estimateAprovisionamientos = useMemo(() => {
    return getEstimateMeses(cuentasAprovisionamientos);
  }, [estimateData]);

  const estimateMercaderias = useMemo(() => {
    return getEstimateMeses(cuentasMercaderias);
  }, [estimateData]);

  const estimateMateriasPrimas = useMemo(() => {
    return getEstimateMeses(cuentasMateriasPrimas);
  }, [estimateData]);

  const estimateTrabajosExternos = useMemo(() => {
    return getEstimateMeses(cuentasTrabajosExternos);
  }, [estimateData]);

  const estimateTrabajosActivo = useMemo(() => {
    return getEstimateMeses(["73000000"]);
  }, [estimateData]);

  const estimateGastosPersonal = useMemo(() => {
    return getEstimateMeses([
      ...cuentasSueldos,
      ...cuentasCargasSociales,
      ...cuentasProvisiones,
    ]);
  }, [estimateData]);

  const estimateOtrosGastosExplotacion = useMemo(() => {
    return getEstimateMeses([
      ...cuentasServiciosExteriores,
      ...cuentasTributos,
      ...cuentasProvisionesComerciales,
      ...cuentasOtrosGastosGestion,
    ]);
  }, [estimateData]);

  const estimateProvisionesComerciales = useMemo(() => {
    return getEstimateMeses(cuentasProvisionesComerciales);
  }, [estimateData]);

  const estimateValorRazonable = useMemo(() => {
    return getEstimateMeses(["76900000", "66900000", "76600000"]);
  }, [estimateData]);

  const cuentasSubvencionesInmovilizado = [
    "74600000", // Subvenciones de inmovilizado no financiero
  ];

  const cuentasEnajenacionesA = ["67000000"];

  const cuentasEnajenacionesB = ["77100000", "67100000"];

  const estimateA1Explotacion = useMemo(() => {
    const resultado = {};
    let totalAnual = 0;

    for (let mes = 1; mes <= 12; mes++) {
      const ventasMes = estimateVentas[mes] || 0;
      const serviciosMes = estimateServicios[mes] || 0;
      const variacionExistencias = estimateVariacionExistencias[mes] || 0;
      const trabajosEmpresaActivo = getEstimateMeses(["73000000"])[mes] || 0;
      const aprovisionamientos = estimateAprovisionamientos[mes] || 0;
      const otrosIngresos = getEstimateMeses(cuentasOtrosIngresos)[mes] || 0;
      const gastosPersonal =
        getEstimateMeses([
          ...cuentasSueldos,
          ...cuentasCargasSociales,
          ...cuentasProvisiones,
        ])[mes] || 0;
      const otrosGastos = estimateOtrosGastosExplotacion[mes] || 0;
      const amortizacion = getEstimateMeses(cuentasAmortizacion)[mes] || 0;
      const imputacionSubvenciones = getEstimateMeses(["74600000"])[mes] || 0;
      const excesosProvisiones = getEstimateMeses(["79400000"])[mes] || 0;
      const resultadoInmovilizado = getEstimateMeses(["67000000"])[mes] || 0;
      const diferenciaNegativa = 0;
      const otrosResultados =
        getEstimateMeses(cuentasOtrosResultados)[mes] || 0;

      const resultadoMes =
        ventasMes +
        serviciosMes +
        variacionExistencias +
        trabajosEmpresaActivo +
        aprovisionamientos +
        otrosIngresos +
        gastosPersonal +
        otrosGastos +
        amortizacion +
        imputacionSubvenciones +
        excesosProvisiones +
        resultadoInmovilizado +
        diferenciaNegativa +
        otrosResultados;

      resultado[mes] = resultadoMes;
      totalAnual += resultadoMes;
    }

    resultado.total = totalAnual;
    return resultado;
  }, [estimateData]);

  const resultadoDeudasGrupo = useMemo(() => {
    return sumarPorCuentasPorMes(cuentasDeudasGrupo, accData);
  }, [accData]);

  const resultadoActualizacionProvisiones = useMemo(() => {
    return sumarPorCuentasPorMes(cuentasActualizacionProvisiones, accData);
  }, [accData]);

  const resultado14IngresosFinancieros = useMemo(() => {
    return calcularResumen("14. Ingresos financieros.", estructura, accData);
  }, [estructura, accData]);

  const resultado15GastosFinancieros = useMemo(() => {
    const resultado = {};
    let totalAnual = 0;

    for (let mes = 1; mes <= 12; mes++) {
      const sumaMes =
        (resultadoDeudasGrupo[mes] || 0) +
        (resultadoDeudasTerceros[mes] || 0) +
        (resultadoActualizacionProvisiones[mes] || 0);

      resultado[mes] = sumaMes;
      totalAnual += sumaMes;
    }

    return { ...resultado, total: totalAnual };
  }, [
    resultadoDeudasGrupo,
    resultadoDeudasTerceros,
    resultadoActualizacionProvisiones,
  ]);

  const resultadoA2Financiero = useMemo(() => {
    const resultado = {};
    let totalAnual = 0;

    for (let mes = 1; mes <= 12; mes++) {
      const sumaMes =
        (resultado14IngresosFinancieros[mes] || 0) +
        (resultado15GastosFinancieros[mes] || 0) +
        (calcularResumen(
          "16. Variación del valor razonable en instrumentos financieros.",
          estructura,
          accData,
        )[mes] || 0) +
        (sumarPorCuentasPorMes(
          ["66810000", "76810000", "66800000", "76800000"],
          accData,
        )[mes] || 0); // 17

      resultado[mes] = sumaMes;
      totalAnual += sumaMes;
    }

    resultado.total = totalAnual;
    return resultado;
  }, [
    estructura,
    accData,
    resultado14IngresosFinancieros,
    resultado15GastosFinancieros,
  ]);

  const estimateA2Financiero = useMemo(() => {
    const resultado = {};
    let totalAnual = 0;

    for (let mes = 1; mes <= 12; mes++) {
      const ingresosFinancieros =
        calcularResumen("14. Ingresos financieros.", estructura, estimateData)[
          mes
        ] || 0;

      const gastosFinancieros =
        getEstimateMeses([
          ...cuentasDeudasGrupo,
          ...cuentasDeudasTerceros,
          ...cuentasActualizacionProvisiones,
        ])[mes] || 0;

      const variacionValorRazonable =
        calcularResumen(
          "16. Variación del valor razonable en instrumentos financieros.",
          estructura,
          estimateData,
        )[mes] || 0;

      const diferenciasCambio =
        getEstimateMeses(cuentasDiferenciasCambio)[mes] || 0;

      const sumaMes =
        ingresosFinancieros +
        gastosFinancieros +
        variacionValorRazonable +
        diferenciasCambio;

      resultado[mes] = sumaMes;
      totalAnual += sumaMes;
    }

    resultado.total = totalAnual;
    return resultado;
  }, [estimateData, estructura]);

  /*   const resultadoFinanciero = useMemo(() => {
    return calcularResumen("B) RESULTADO FINANCIERO", estructura, accData, {
      "A.2) RESULTADO FINANCIERO": resultadoA2Financiero,
    });
  }, [estructura, accData, resultadoA2Financiero]); */

  const resultadoDiferenciasCambio = useMemo(() => {
    return sumarPorCuentasPorMes(cuentasDiferenciasCambio, accData);
  }, [accData]);

  const resultadoAntesImpuestos = useMemo(() => {
    const meses = Object.keys(resultadoA1Explotacion || {});
    const resumen = {};
    let total = 0;

    meses.forEach((mes) => {
      const valor =
        (resultadoA1Explotacion?.[mes] || 0) +
        (resultadoA2Financiero?.[mes] || 0);
      resumen[mes] = valor;
      total = valor;
    });

    resumen.total = total;
    return resumen;
  }, [resultadoA1Explotacion, resultadoA2Financiero]);

  const estimateAntesImpuestos = useMemo(() => {
    const resumen = {};
    let total = 0;

    meses.forEach((mes) => {
      const valor =
        (estimateA1Explotacion?.[mes] || 0) +
        (estimateA2Financiero?.[mes] || 0);

      resumen[mes] = valor;
      total += valor;
    });

    resumen.total = total;
    return resumen;
  }, [estimateA1Explotacion, estimateA2Financiero]);

  const estimateDeudasTerceros = useMemo(() => {
    return getEstimateMeses(cuentasDeudasTerceros);
  }, [estimateData]);

  const estimateDeudasGrupo = useMemo(() => {
    return getEstimateMeses(cuentasDeudasGrupo);
  }, [estimateData]);

  const estimateActualizacionProvisiones = useMemo(() => {
    return getEstimateMeses(cuentasActualizacionProvisiones);
  }, [estimateData]);

  const estimateDiferenciasCambio = useMemo(() => {
    return getEstimateMeses(cuentasDiferenciasCambio);
  }, [estimateData]);

  /* proyecciones venta y coste*/
  const calcularProyeccionPorMes = (datos) => {
    const resultado = {};
    let total = 0;

    meses.forEach((mes) => {
      const mesNum = parseInt(mes);
      const suma = datos
        .filter(
          (d) =>
            Number(
              d?.mes ?? d?.MES ?? d?.MES_ENTREGA ?? d?.MexCEX ?? d?.MesCEX,
            ) === mesNum,
        )
        .reduce((acc, d) => {
          const raw = d?.valor ?? d?.VALOR ?? d?.Base ?? d?.BASE ?? d?.base;
          const num =
            typeof raw === "string"
              ? parseFloat(raw.replace(",", "."))
              : raw || 0;
          return acc + num;
        }, 0);

      resultado[mes] = suma;
      total += suma;
    });

    resultado.total = total;
    return resultado;
  };

  const resultadoProyeccionVentas = useMemo(
    () => calcularProyeccionPorMes(proyeccionVentas),
    [proyeccionVentas],
  );

  const resultadoProyeccionCompras = useMemo(
    () => calcularProyeccionPorMes(proyeccionCompras),
    [proyeccionCompras],
  );

  const resultadoPedidos_Ventas = useMemo(
    () => calcularProyeccionPorMes(pedidos_venta),
    [pedidos_venta],
  );

  const resultadoPedidos_Compras = useMemo(
    () => calcularProyeccionPorMes(pedidos_compra),
    [pedidos_compra],
  );

  const resultadoFacturasBorrador_Ventas = useMemo(
    () => calcularProyeccionPorMes(facturas_venta_borrador),
    [facturas_venta_borrador],
  );

  /* proyecciones venta y coste*/

  const resultadoFinal = useMemo(() => {
    const resultado = {};
    let totalReal = 0;
    let totalEstimate = 0;

    meses.forEach((mes) => {
      const realC = resultadoAntesImpuestos[mes] || 0;
      const estC = estimateAntesImpuestos[mes] || 0;
      const realVentas = resultadoProyeccionVentas[mes] || 0;
      const realCompras = resultadoProyeccionCompras[mes] || 0;
      const realPedidosVenta = resultadoPedidos_Ventas[mes] || 0;
      const realPedidosCompra = resultadoPedidos_Compras[mes] || 0;
      const realFacturasBorradorVenta =
        resultadoFacturasBorrador_Ventas[mes] || 0;

      const real =
        realC +
        realVentas +
        realCompras +
        realPedidosVenta +
        realPedidosCompra +
        realFacturasBorradorVenta;

      const estimate = estC; // sigue siendo el mismo
      const diff = real - estimate;

      resultado[mes] = { real, estimate, diff };

      totalReal += real;
      totalEstimate += estimate;
    });

    resultado.total = {
      real: totalReal,
      estimate: totalEstimate,
      diff: totalReal - totalEstimate,
    };

    return resultado;
  }, [
    resultadoAntesImpuestos,
    estimateAntesImpuestos,
    resultadoProyeccionVentas,
    resultadoProyeccionCompras,
    resultadoPedidos_Ventas,
    resultadoPedidos_Compras,
    resultadoFacturasBorrador_Ventas,
  ]);

  /** Margen comercial mensual: cifra de negocios + coste producto + pipeline (proy./pedidos). */
  const margenMensual = useMemo(() => {
    const resultado = {};
    let totalReal = 0;
    let totalEstimate = 0;

    meses.forEach((mes) => {
      const m = parseInt(mes, 10);
      const variacionExistencias = accData
        .filter((a) => a.codigo === "71000000" && a.mes === m)
        .reduce((sum, a) => sum + (a.valor || 0), 0);
      const trabajosActivo = accData
        .filter((a) => a.codigo === "73000000" && a.mes === m)
        .reduce((sum, a) => sum + (a.valor || 0), 0);
      const trabajosExternos = cuentasTrabajosExternos.reduce((acc, codigo) => {
        const match = accData.find((a) => a.codigo === codigo && a.mes === m);
        return acc + (match?.valor || 0);
      }, 0);

      const real =
        (sumaVentas[mes] || 0) +
        (sumaServicios[mes] || 0) +
        variacionExistencias +
        trabajosActivo +
        (totalAprovisionamientos[mes] || 0) +
        trabajosExternos +
        (resultadoProyeccionVentas[mes] || 0) +
        (resultadoProyeccionCompras[mes] || 0) +
        (resultadoPedidos_Ventas[mes] || 0) +
        (resultadoPedidos_Compras[mes] || 0) +
        (resultadoFacturasBorrador_Ventas[mes] || 0);

      const estimate =
        (estimateVentas[mes] || 0) +
        (estimateServicios[mes] || 0) +
        (estimateVariacionExistencias[mes] || 0) +
        (estimateTrabajosActivo[mes] || 0) +
        (estimateAprovisionamientos[mes] || 0) +
        (estimateTrabajosExternos[mes] || 0);

      resultado[mes] = { real, estimate, diff: real - estimate };
      totalReal += real;
      totalEstimate += estimate;
    });

    resultado.total = {
      real: totalReal,
      estimate: totalEstimate,
      diff: totalReal - totalEstimate,
    };
    return resultado;
  }, [
    accData,
    sumaVentas,
    sumaServicios,
    totalAprovisionamientos,
    estimateVentas,
    estimateServicios,
    estimateVariacionExistencias,
    estimateTrabajosActivo,
    estimateAprovisionamientos,
    estimateTrabajosExternos,
    resultadoProyeccionVentas,
    resultadoProyeccionCompras,
    resultadoPedidos_Ventas,
    resultadoPedidos_Compras,
    resultadoFacturasBorrador_Ventas,
  ]);

  const exportarExcelVisual = () => {
    const wb = XLSX.utils.book_new();
    const hoja = [];

    const limpiar = (valor) => {
      if (valor === undefined || valor === null || valor === "" || isNaN(valor))
        return "";
      return typeof valor === "number"
        ? Number(valor.toFixed(2))
        : String(valor);
    };

    const addLinea = (nombre, datos, estimate = null, indent = 0) => {
      const fila = { Línea: `${"  ".repeat(indent)}${nombre}` };

      meses.forEach((mes, i) => {
        const mesLabel = `M${i + 1}`;
        const real = limpiar(datos?.[mes]);
        const est = limpiar(estimate?.[mes] || 0);
        const diff =
          typeof real === "number" && typeof est === "number"
            ? limpiar(real - est)
            : "";

        fila[`${mesLabel} Real`] = real;
        fila[`${mesLabel} Est`] = est;
        fila[`${mesLabel} Dif`] = diff;
      });

      const totalReal = limpiar(datos?.total);
      const totalEst = limpiar(estimate?.total || 0);
      const totalDiff =
        typeof totalReal === "number" && typeof totalEst === "number"
          ? limpiar(totalReal - totalEst)
          : "";

      fila["Total Real"] = totalReal;
      fila["Total Est"] = totalEst;
      fila["Total Dif"] = totalDiff;

      hoja.push(fila);
    };

    const addSubcuentas = (cuentas, indent = 2) => {
      cuentas.forEach((codigo) => {
        const titulo =
          estructura.find((e) => e.codigo === codigo)?.titulo ||
          titulosCuentaPorCodigo[codigo] ||
          codigo;
        const realData = sumarPorCuentasPorMes([codigo], accData);
        const estData = getEstimateMeses([codigo]);
        addLinea(titulo, realData, estData, indent);
      });
    };

    const secciones = [
      {
        nombre: "VENTAS",
        lineas: [
          {
            nombre: "a) Ventas",
            cuentas: cuentasVentas,
            datos: sumaVentas,
            estimate: estimateVentas,
          },
        ],
      },
      {
        nombre: "SERVICIOS",
        lineas: [
          {
            nombre: "b) Servicios",
            cuentas: cuentasServicios,
            datos: sumaServicios,
            estimate: estimateServicios,
          },
        ],
      },
      {
        nombre: "APROVISIONAMIENTOS",
        lineas: [
          {
            nombre: "4. Aprovisionamientos",
            cuentas: [...cuentasMercaderias, ...cuentasMateriasPrimas],
            datos: totalAprovisionamientos,
            estimate: estimateAprovisionamientos,
          },
        ],
      },
      {
        nombre: "6. Gastos de personal",
        datos: resultadoGastosPersonal,
        estimate: estimateGastosPersonal,
        lineas: [
          {
            nombre: "a) Sueldos, salarios y asimilados",
            cuentas: cuentasSueldos,
          },
          {
            nombre: "b) Cargas sociales",
            cuentas: cuentasCargasSociales,
          },
          {
            nombre: "c) Provisiones",
            cuentas: cuentasProvisiones,
          },
        ],
      },
      {
        nombre: "OTROS GASTOS DE EXPLOTACIÓN",
        lineas: [
          {
            nombre: "Otros gastos de explotación",
            cuentas: [
              ...cuentasServiciosExteriores,
              ...cuentasTributos,
              ...cuentasOtrosGastosGestion,
            ],
            datos: resultadoOtrosGastosExplotacion,
            estimate: estimateOtrosGastosExplotacion,
          },
        ],
      },
      {
        nombre: "8. Amortización del inmovilizado",
        cuentas: cuentasAmortizacion,
      },
      {
        nombre:
          "9. Imputación de subvenciones de inmovilizado no financiero y otras",
        cuentas: ["74600000"],
      },
      {
        nombre: "10. Excesos de provisiones",
        cuentas: ["79400000"],
      },
      {
        nombre: "11. Deterioro y resultado por enajenaciones del inmovilizado",
        cuentas: ["67000000"],
      },
      {
        nombre: "12. Otros resultados",
        cuentas: cuentasOtrosResultados,
        datos: calcularResumen("12. Otros resultados", estructura, accData),
      },
      {
        nombre: "A.1) RESULTADO DE EXPLOTACIÓN",
        datos: resultadoA1Explotacion,
        estimate: estimateA1Explotacion,
      },
      {
        nombre: "14. Ingresos financieros.",
        cuentas: [
          "76900000",
          "76600000",
          "76810000",
          "76800000",
          "76200000",
          "76000000",
        ],
        datos: resultado14IngresosFinancieros,
      },
      {
        nombre: "15. Gastos financieros",
        cuentas: [
          ...cuentasDeudasGrupo,
          ...cuentasDeudasTerceros,
          ...cuentasActualizacionProvisiones,
        ],
        datos: resultado15GastosFinancieros,
      },
      {
        nombre:
          "16. Variación del valor razonable en instrumentos financieros.",
        cuentas: ["66900000"],
        datos: calcularResumen(
          "16. Variación del valor razonable en instrumentos financieros.",
          estructura,
          accData,
        ),
      },
      {
        nombre: "17. Diferencias de cambio",
        cuentas: cuentasDiferenciasCambio,
        datos: resultadoDiferenciasCambio,
      },
      {
        nombre:
          "18. Deterioro y resultado por enajenaciones de instrumentos financieros",
        cuentas: ["66700000", "66710000"],
      },
      {
        nombre: "19. Otros ingresos y gastos de carácter financiero",
        cuentas: ["76500000", "76510000"],
      },
      {
        nombre: "A.2) RESULTADO FINANCIERO",
        datos: resultadoA2Financiero,
        estimate: estimateA2Financiero,
      },
      {
        nombre: "C) RESULTADO ANTES DE IMPUESTOS",
        datos: resultadoAntesImpuestos,
        estimate: estimateAntesImpuestos,
      },
      {
        nombre: "PROYECCIÓN VENTAS",
        datos: resultadoProyeccionVentas,
      },
      {
        nombre: "PROYECCIÓN COMPRAS",
        datos: resultadoProyeccionCompras,
      },
      {
        nombre: "PEDIDOS DE VENTA",
        datos: resultadoPedidos_Ventas,
      },
      {
        nombre: "FACTURAS BORRADOR VENTA",
        datos: resultadoFacturasBorrador_Ventas,
      },
      {
        nombre: "PEDIDOS DE COMPRA",
        datos: resultadoPedidos_Compras,
      },
      {
        nombre: "RESULTADO FINAL",
        datos: resultadoFinal.total
          ? resultadoFinal
          : { total: { real: 0, estimate: 0, diff: 0 } },
      },
    ];

    secciones.forEach((seccion) => {
      if (seccion.nombre) hoja.push({ Línea: `▶ ${seccion.nombre}` });

      if (seccion.datos) {
        addLinea(seccion.nombre, seccion.datos, seccion.estimate, 1);
      }

      if (seccion.lineas) {
        seccion.lineas.forEach((linea) => {
          if (linea.datos) {
            addLinea(linea.nombre, linea.datos, linea.estimate, 1);
          } else {
            addLinea(linea.nombre, {}, {}, 1);
          }

          if (linea.cuentas) {
            addSubcuentas(linea.cuentas, 2);
          }
        });
      }

      if (seccion.cuentas) {
        addSubcuentas(seccion.cuentas);
      }

      hoja.push({});
    });

    const ws = XLSX.utils.json_to_sheet(hoja);
    XLSX.utils.book_append_sheet(wb, ws, "PyG Visual");
    XLSX.writeFile(wb, `PyG_${selectedYear}_Visual.xlsx`);
  };

  const getEstiloValor = (valor) => ({
    color:
      valor === 0 || valor === null || valor === undefined
        ? "#dddcdc"
        : valor >= 0
          ? "rgb(76, 98, 100)"
          : "orange",
    fontWeight: 600,
    fontSize: "13px",
  });

  const getEstiloValor1 = (valor) => ({
    color:
      valor === 0 || valor === null || valor === undefined
        ? "#c6c6c6"
        : valor >= 0
          ? "rgb(76, 98, 100)"
          : "orange",
    fontWeight: 800,
    fontSize: "15px",
  });

  const getEstiloValorEstimate = (valor) => ({
    color:
      valor === 0 || valor === null || valor === undefined
        ? "#c6c6c6"
        : valor >= 0
          ? "rgb(19, 136, 123)"
          : "red",
    fontWeight: 600,
    fontSize: "13px",
  });

  const getEstiloValorEstimateHead = (valor) => ({
    color:
      valor === 0 || valor === null || valor === undefined
        ? "#c6c6c6"
        : valor >= 0
          ? "rgb(19, 136, 123)"
          : "red",
    fontWeight: 800,
    fontSize: "15px",
  });

  const formatearMiles = (valor) => {
    const partes = Math.abs(valor).toFixed(2).split(".");
    const enteroConPuntos = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const resultado =
      (valor < 0 ? "-" : "") + enteroConPuntos + "," + partes[1];
    return resultado;
  };
  const epigrafesRealPpto = useMemo(
    () => [
      {
        id: "ventas",
        label: "a) Ventas",
        serie: toRealPptoSerie(sumaVentas, estimateVentas),
      },
      {
        id: "aprovisionamientos",
        label: "4. Aprovisionamientos.",
        serie: toRealPptoSerie(
          totalAprovisionamientos,
          estimateAprovisionamientos,
        ),
      },
      {
        id: "gastos-personal",
        label: "6. Gastos de personal",
        serie: toRealPptoSerie(resultadoGastosPersonal, estimateGastosPersonal),
      },
      {
        id: "otros-gastos",
        label: "7. Otros gastos de explotación",
        serie: toRealPptoSerie(
          resultadoOtrosGastosExplotacion,
          estimateOtrosGastosExplotacion,
        ),
      },
      {
        id: "a2-financiero",
        label: "A.2) RESULTADO FINANCIERO",
        serie: toRealPptoSerie(resultadoA2Financiero, estimateA2Financiero),
      },
      {
        id: "resultado-final",
        label: "RESULTADO FINAL",
        serie: resultadoFinal,
      },
    ],
    [
      sumaVentas,
      estimateVentas,
      totalAprovisionamientos,
      estimateAprovisionamientos,
      resultadoGastosPersonal,
      estimateGastosPersonal,
      resultadoOtrosGastosExplotacion,
      estimateOtrosGastosExplotacion,
      resultadoA2Financiero,
      estimateA2Financiero,
      resultadoFinal,
    ],
  );

  const totalRealResumen = Number(resultadoFinal?.total?.real || 0);
  const totalPresResumen = Number(resultadoFinal?.total?.estimate || 0);
  const totalDiffResumen = Number(resultadoFinal?.total?.diff || 0);
  const resumenDiffColor =
    totalDiffResumen > 0
      ? "success.main"
      : totalDiffResumen < 0
        ? "error.main"
        : "text.secondary";

  return (
    <Box sx={{ p: { xs: 1.25, sm: 2 }, ...contentMargin }}>
      <Box
        sx={{
          p: { xs: 1.25, sm: 1.75 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.8),
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
              : "linear-gradient(135deg, #eef9fb 0%, #f4f0ff 60%, #f0faf4 100%)",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 8px 24px rgba(0,0,0,0.35)"
              : "0 8px 24px rgba(15,23,42,0.08)",
          mb: 1.5,
        }}
      >
        <Stack spacing={1.1}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ lineHeight: 1.1, letterSpacing: 1.1, fontWeight: 700 }}
              >
                FINANCE · PYG DEBUG
              </Typography>
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{ lineHeight: 1.1 }}
              >
                {titulo} {selectedYear}
              </Typography>
              <Stack direction="row" spacing={0.7} sx={{ mt: 0.5 }} useFlexGap>
                <Chip
                  size="small"
                  icon={<AutoGraphOutlinedIcon sx={{ fontSize: 16 }} />}
                  label={`Año ${selectedYear}`}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    EMPRESA_OPTIONS.find((opt) => opt.value === empresa)
                      ?.label || empresa
                  }
                  sx={{ fontWeight: 700 }}
                />
                {empresa === "fusion" ? (
                  <Chip
                    size="small"
                    color="secondary"
                    variant="outlined"
                    label="Consolidado Stage + ERNA"
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
              </Stack>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              useFlexGap
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: 170 } }}
              >
                <InputLabel>Empresa</InputLabel>
                <Select
                  value={empresa}
                  label="Empresa"
                  onChange={(e) => onEmpresaChange(String(e.target.value))}
                  sx={{
                    backgroundColor: alpha(
                      theme.palette.background.paper,
                      0.92,
                    ),
                    borderRadius: 1.5,
                  }}
                >
                  {EMPRESA_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: 120 } }}
              >
                <InputLabel>Año</InputLabel>
                <Select
                  value={selectedYear}
                  label="Año"
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={{
                    backgroundColor: alpha(
                      theme.palette.background.paper,
                      0.92,
                    ),
                    borderRadius: 1.5,
                  }}
                >
                  {[2023, 2024, 2025, 2026].map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="inherit"
                onClick={exportarExcelVisual}
                startIcon={<DownloadForOfflineOutlinedIcon />}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.95),
                  borderRadius: 1.5,
                  px: 1.5,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.background.paper, 1),
                  },
                }}
              >
                Exportar Excel
              </Button>
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={0.8}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            <Chip
              size="small"
              icon={<TrendingUpOutlinedIcon sx={{ fontSize: 16 }} />}
              label={`Real total: ${formatearMiles(totalRealResumen)}`}
              sx={{
                fontWeight: 800,
                bgcolor: alpha(theme.palette.info.main, 0.14),
                color: "info.dark",
              }}
            />
            <Chip
              size="small"
              label={`Presupuesto: ${formatearMiles(totalPresResumen)}`}
              sx={{
                fontWeight: 800,
                bgcolor: alpha(theme.palette.warning.main, 0.14),
                color: "warning.dark",
              }}
            />
            <Chip
              size="small"
              icon={
                totalDiffResumen >= 0 ? (
                  <TrendingUpOutlinedIcon sx={{ fontSize: 16 }} />
                ) : (
                  <TrendingDownOutlinedIcon sx={{ fontSize: 16 }} />
                )
              }
              label={`Diferencia: ${formatearMiles(totalDiffResumen)}`}
              sx={{
                fontWeight: 900,
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                color: resumenDiffColor,
                border: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.95),
              }}
            />
          </Stack>
        </Stack>
      </Box>

      <PygResultadoFinalCharts
        resultadoFinal={resultadoFinal}
        margenMensual={margenMensual}
        formatearMiles={formatearMiles}
      />

      <PygEpigrafesRealPptoChart
        epigrafes={epigrafesRealPpto}
        formatearMiles={formatearMiles}
      />

      <TableContainer
        component={Paper}
        sx={{
          marginTop: 1,
          maxHeight: "87vh",
          overflowX: "auto",
          overflowY: "auto",
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.8),
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 10px 24px rgba(0,0,0,0.34)"
              : "0 10px 24px rgba(15,23,42,0.08)",
          backgroundColor: alpha(theme.palette.background.paper, 0.97),
        }}
      >
        <Table
          size="small"
          sx={{
            "& .MuiTableCell-root": {
              py: 0.65,
              borderBottomColor: alpha(theme.palette.divider, 0.6),
            },
            "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)": {
              backgroundColor: alpha(theme.palette.background.default, 0.28),
            },
            "& .MuiTableBody-root .MuiTableRow-root:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            },
            "& .MuiTableCell-root:first-of-type": {
              boxShadow: `2px 0 0 ${alpha(theme.palette.divider, 0.55)}`,
            },
            "& .MuiTableBody-root .MuiTableCell-root:first-of-type": {
              zIndex: 1,
            },
          }}
        >
          <TableHead
            sx={{
              borderLeft: `1px solid ${theme.palette.pyg}`,
              position: "sticky",
              top: 0,
              backgroundColor: alpha(theme.palette.background.paper, 0.98),
              backdropFilter: "blur(4px)",
              zIndex: 2,
              "& .MuiTableCell-root": {
                fontWeight: 800,
                letterSpacing: 0.25,
              },
            }}
          >
            <TableRow>
              <TableCell
                style={{
                  position: "sticky",
                  top: 0,
                  left: 0,
                  backgroundColor: theme.palette.background.paper,
                  zIndex: 1,
                }}
              >
                <strong>Título</strong>
              </TableCell>
              {meses.map((mes) => (
                <TableCell
                  key={`mes-${mes}`}
                  align="center"
                  colSpan={3}
                  sx={{
                    borderLeft: `1px solid ${theme.palette.pyg}`,
                  }}
                >
                  <strong>{mes}</strong>
                </TableCell>
              ))}
              <TableCell
                align="center"
                colSpan={3}
                sx={{
                  borderLeft: `1px solid ${theme.palette.pyg}`,
                }}
              >
                <strong>Total</strong>
              </TableCell>
            </TableRow>
            <TableRow
              sx={{
                position: "sticky",
                top: 32,
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
              }}
            >
              <TableCell
                sx={{
                  position: "sticky",
                  top: 32,
                  left: 0,
                  backgroundColor: theme.palette.background.paper,
                  zIndex: 1,
                }}
              ></TableCell>
              {meses.map((mes) => (
                <>
                  <TableCell
                    key={`real-${mes}`}
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <strong>Real</strong>
                  </TableCell>
                  <TableCell key={`est-${mes}`} align="right">
                    <strong>Pres.</strong>
                  </TableCell>
                  <TableCell key={`diff-${mes}`} align="right">
                    <strong>Dif.</strong>
                  </TableCell>
                </>
              ))}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <strong>Real</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Pres.</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Dif.</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* a) Ventas. */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("ventas")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["ventas"] ? "▼" : "►"} a) Ventas.
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = sumaVentas[mes] || 0;
                const estimate = estimateVentas[mes] || 0;
                const diferencia = -estimate + real;
                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diferencia)}>
                        {formatearMiles(diferencia)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span style={getEstiloValorEstimateHead(sumaVentas.total)}>
                  {formatearMiles(sumaVentas.total)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span style={getEstiloValor1(estimateVentas.total)}>
                  {formatearMiles(estimateVentas.total)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    -estimateVentas.total + sumaVentas.total,
                  )}
                >
                  {formatearMiles(-estimateVentas.total + sumaVentas.total)}
                </span>
              </TableCell>
            </TableRow>

            {/* Hijos de a) Ventas */}
            {gruposAbiertos["ventas"] &&
              cuentasVentas.map((codigo) => {
                const lineaReal = accData.filter((a) => a.codigo === codigo);
                const lineaEst = estimateData.filter(
                  (e) => e.codigo === codigo,
                );

                const valoresMesReal = {};
                const valoresMesEst = {};
                const valoresMesDiff = {};

                let totalReal = 0;
                let totalEst = 0;
                let totalDiff = 0;

                meses.forEach((mes) => {
                  const valorReal =
                    Number(
                      lineaReal.find((a) => a.mes === parseInt(mes))?.valor,
                    ) || 0;
                  const valorEst =
                    Number(
                      lineaEst.find((e) => e.mes === parseInt(mes))?.valor,
                    ) || 0;
                  const valorDiff = -valorEst + valorReal;

                  valoresMesReal[mes] = valorReal;
                  valoresMesEst[mes] = valorEst;
                  valoresMesDiff[mes] = valorDiff;

                  totalReal += valorReal;
                  totalEst += valorEst;
                  totalDiff -= valorDiff;
                });

                const titulo =
                  estructura.find((e) => e.codigo === codigo)?.titulo || codigo;

                return (
                  <TableRow key={codigo}>
                    <TableCell
                      style={{
                        paddingLeft: "2em",
                        position: "sticky",
                        left: 0,
                        backgroundColor: ` ${theme.palette.background.paper}`,
                      }}
                    >
                      {titulo}
                    </TableCell>
                    {meses.map((mes) => (
                      <>
                        <TableCell
                          key={`real-${codigo}-${mes}`}
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span
                            style={getEstiloValorEstimate(valoresMesReal[mes])}
                          >
                            {formatearMiles(valoresMesReal[mes])}
                          </span>
                        </TableCell>
                        <TableCell key={`est-${codigo}-${mes}`} align="right">
                          <span style={getEstiloValor(valoresMesEst[mes])}>
                            {formatearMiles(valoresMesEst[mes])}
                          </span>
                        </TableCell>
                        <TableCell key={`diff-${codigo}-${mes}`} align="right">
                          <span style={getEstiloValor(valoresMesDiff[mes])}>
                            {formatearMiles(valoresMesDiff[mes])}
                          </span>
                        </TableCell>
                      </>
                    ))}
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimate(totalReal)}>
                        {formatearMiles(totalReal)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor(totalEst)}>
                        {formatearMiles(totalEst)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      {/* <span style={getEstiloValor(totalDiff)}>
                        {formatearMiles(totalDiff)}
                      </span> */}
                      <span style={getEstiloValor(-totalEst + totalReal)}>
                        {formatearMiles(-totalEst + totalReal)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}

            {/* b) Prestaciones de servicios. */}
            <TableRow
              style={{
                backgroundColor: ` ${theme.palette.estimates.main}`,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("prestaciones")}
            >
              <TableCell
                style={{
                  paddingLeft: "2em",
                  position: "sticky",
                  left: 0,
                  backgroundColor: ` ${theme.palette.estimates.main}`,
                }}
              >
                <strong>
                  {gruposAbiertos["prestaciones"] ? "▼" : "►"} b) Prestaciones
                  de servicios.
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = sumaServicios[mes] || 0;
                const est = estimateServicios[mes] || 0;
                const diff = -est + real;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(est)}>
                        {formatearMiles(est)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span style={getEstiloValorEstimateHead(sumaServicios.total)}>
                  {formatearMiles(sumaServicios.total || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span style={getEstiloValor1(estimateServicios.total)}>
                  {formatearMiles(estimateServicios.total || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    -estimateServicios.total + sumaServicios.total,
                  )}
                >
                  {formatearMiles(
                    (-estimateServicios.total || 0) +
                      (sumaServicios.total || 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* Hijos de b) Prestaciones */}
            {gruposAbiertos["prestaciones"] &&
              cuentasServicios.map((codigo) => {
                const linea = accData.filter((a) => a.codigo === codigo);
                const lineaPresupuesto = estimateData.filter(
                  (e) => e.codigo === codigo,
                );

                const valoresMes = {};
                const estimadosMes = {};
                let total = 0;
                let totalEst = 0;

                meses.forEach((mes) => {
                  const valor =
                    Number(linea.find((a) => a.mes === parseInt(mes))?.valor) ||
                    0;
                  const est =
                    Number(
                      lineaPresupuesto.find((e) => e.mes === parseInt(mes))
                        ?.valor,
                    ) || 0;

                  valoresMes[mes] = valor;
                  estimadosMes[mes] = est;

                  total += valor;
                  totalEst += est;
                });

                const titulo =
                  estructura.find((e) => e.codigo === codigo)?.titulo || codigo;

                return (
                  <TableRow key={codigo}>
                    <TableCell
                      style={{
                        paddingLeft: "2em",

                        position: "sticky",
                        left: 0,
                        backgroundColor: ` ${theme.palette.background.paper}`,
                      }}
                    >
                      {titulo}
                    </TableCell>
                    {meses.map((mes) => {
                      const real = valoresMes[mes];
                      const est = estimadosMes[mes];
                      const diff = est - real;

                      return (
                        <React.Fragment key={mes}>
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span style={getEstiloValorEstimate(real)}>
                              {formatearMiles(real)}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(est)}>
                              {formatearMiles(est)}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(diff)}>
                              {formatearMiles(diff)}
                            </span>
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimate(total)}>
                        {formatearMiles(total)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor(totalEst)}>
                        {formatearMiles(totalEst)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor(totalEst - total)}>
                        {formatearMiles(totalEst - total)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}

            {/* 2. Variación de existencias... */}
            <TableRow
              style={{
                backgroundColor: ` ${theme.palette.estimates.main}`,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("variacionExistencias")}
            >
              <TableCell
                style={{
                  paddingLeft: "2em",
                  position: "sticky",
                  left: 0,
                  backgroundColor: ` ${theme.palette.estimates.main}`,
                }}
              >
                <strong>
                  {gruposAbiertos["variacionExistencias"] ? "▼" : "►"} 2.
                  Variación de existencias de productos terminados y en curso de
                  fabricación.
                </strong>
              </TableCell>

              {meses.map((mes) => {
                const real = accData
                  .filter(
                    (a) => a.codigo === "71000000" && a.mes === parseInt(mes),
                  )
                  .reduce((acc, a) => acc + (a.valor || 0), 0);

                const estimate = estimateVariacionExistencias[mes] || 0;
                const diferencia = -estimate + real;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValor1(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diferencia)}>
                        {formatearMiles(diferencia)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}

              {/* Totales al final */}
              {(() => {
                const totalReal = accData
                  .filter((a) => a.codigo === "71000000")
                  .reduce((sum, a) => sum + (a.valor || 0), 0);

                const totalEstimate = estimateVariacionExistencias.total || 0;
                const totalDiferencia = -totalEstimate + totalReal;

                return (
                  <>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValor1(totalReal)}>
                        {formatearMiles(totalReal)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(totalEstimate)}>
                        {formatearMiles(totalEstimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(totalDiferencia)}>
                        {formatearMiles(totalDiferencia)}
                      </span>
                    </TableCell>
                  </>
                );
              })()}
            </TableRow>

            {/* Hijo: cuenta 71000000 */}
            {gruposAbiertos["variacionExistencias"] && (
              <TableRow>
                <TableCell
                  style={{
                    paddingLeft: "2em",
                    position: "sticky",
                    left: 0,
                    backgroundColor: ` ${theme.palette.background.paper}`,
                  }}
                >
                  {estructura.find((e) => e.codigo === "71000000")?.titulo ||
                    "71000000"}
                </TableCell>
                {meses.map((mes) => {
                  const real =
                    accData.find(
                      (a) => a.codigo === "71000000" && a.mes === parseInt(mes),
                    )?.valor || 0;
                  const estimate =
                    estimateData.find(
                      (e) => e.codigo === "71000000" && e.mes === parseInt(mes),
                    )?.valor || 0;
                  const diff = real - estimate;

                  return (
                    <React.Fragment key={mes}>
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValorEstimate(real)}>
                          {formatearMiles(real)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(estimate)}>
                          {formatearMiles(estimate)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(diff)}>
                          {formatearMiles(diff)}
                        </span>
                      </TableCell>
                    </React.Fragment>
                  );
                })}

                {/* Totales */}
                {(() => {
                  const totalReal = accData
                    .filter((a) => a.codigo === "71000000")
                    .reduce((sum, a) => sum + (a.valor || 0), 0);

                  const totalEstimate = estimateVariacionExistencias.total || 0;
                  const totalDiff = totalReal - totalEstimate;

                  return (
                    <>
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValor1(totalReal)}>
                          {formatearMiles(totalReal)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor1(totalEstimate)}>
                          {formatearMiles(totalEstimate)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor1(totalDiff)}>
                          {formatearMiles(totalDiff)}
                        </span>
                      </TableCell>
                    </>
                  );
                })()}
              </TableRow>
            )}

            {/* 3. Trabajos realizados por la empresa para su activo */}
            <TableRow
              style={{
                backgroundColor: ` ${theme.palette.estimates.main}`,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("trabajosActivo")}
            >
              <TableCell
                style={{
                  paddingLeft: "2em",
                  position: "sticky",
                  left: 0,
                  backgroundColor: ` ${theme.palette.estimates.main}`,
                }}
              >
                <strong>
                  {gruposAbiertos["trabajosActivo"] ? "▼" : "►"} 3. Trabajos
                  realizados por la empresa para su activo
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real =
                  accData.find(
                    (a) => a.codigo === "73000000" && a.mes === parseInt(mes),
                  )?.valor || 0;
                const estimate =
                  estimateData.find(
                    (e) => e.codigo === "73000000" && e.mes === parseInt(mes),
                  )?.valor || 0;
                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValor1(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              {(() => {
                const totalReal = accData
                  .filter((a) => a.codigo === "73000000")
                  .reduce((sum, a) => sum + (a.valor || 0), 0);

                const totalEstimate =
                  estimateData
                    .filter((e) => e.codigo === "73000000")
                    .reduce((sum, e) => sum + (e.valor || 0), 0) || 0;

                const totalDiff = totalReal - totalEstimate;

                return (
                  <>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValor1(totalReal)}>
                        {formatearMiles(totalReal)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(totalEstimate)}>
                        {formatearMiles(totalEstimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(totalDiff)}>
                        {formatearMiles(totalDiff)}
                      </span>
                    </TableCell>
                  </>
                );
              })()}
            </TableRow>

            {/* Hijo único: 73000000 */}
            {gruposAbiertos["trabajosActivo"] && (
              <TableRow>
                <TableCell
                  style={{
                    paddingLeft: "2em",
                    position: "sticky",
                    left: 0,
                    backgroundColor: ` ${theme.palette.background.paper}`,
                  }}
                >
                  {estructura.find((e) => e.codigo === "73000000")?.titulo ||
                    "73000000"}
                </TableCell>
                {meses.map((mes) => {
                  const real =
                    accData.find(
                      (a) => a.codigo === "73000000" && a.mes === parseInt(mes),
                    )?.valor || 0;
                  const estimate =
                    estimateData.find(
                      (e) => e.codigo === "73000000" && e.mes === parseInt(mes),
                    )?.valor || 0;
                  const diff = real - estimate;

                  return (
                    <React.Fragment key={mes}>
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValorEstimate(real)}>
                          {formatearMiles(real)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(estimate)}>
                          {formatearMiles(estimate)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(diff)}>
                          {formatearMiles(diff)}
                        </span>
                      </TableCell>
                    </React.Fragment>
                  );
                })}
                {(() => {
                  const totalReal = accData
                    .filter((a) => a.codigo === "73000000")
                    .reduce((sum, a) => sum + (a.valor || 0), 0);

                  const totalEstimate =
                    estimateData
                      .filter((e) => e.codigo === "73000000")
                      .reduce((sum, e) => sum + (e.valor || 0), 0) || 0;

                  const totalDiff = totalReal - totalEstimate;

                  return (
                    <>
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValor(totalReal)}>
                          {formatearMiles(totalReal)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalEstimate)}>
                          {formatearMiles(totalEstimate)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalDiff)}>
                          {formatearMiles(totalDiff)}
                        </span>
                      </TableCell>
                    </>
                  );
                })()}
              </TableRow>
            )}

            {/* 4. Aprovisionamientos. */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("aprovisionamientos")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["aprovisionamientos"] ? "▼" : "►"} 4.
                  Aprovisionamientos.
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = totalAprovisionamientos[mes] || 0;
                const estimate = estimateAprovisionamientos[mes] || 0;
                const diferencia = -estimate + real;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diferencia)}>
                        {formatearMiles(diferencia)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    totalAnualAprovisionamientos,
                  )}
                >
                  {formatearMiles(totalAnualAprovisionamientos || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span style={getEstiloValor1(estimateAprovisionamientos.total)}>
                  {formatearMiles(estimateAprovisionamientos.total || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    -estimateAprovisionamientos.total +
                      totalAnualAprovisionamientos,
                  )}
                >
                  {formatearMiles(
                    -estimateAprovisionamientos.total +
                      totalAnualAprovisionamientos,
                  )}
                </span>
              </TableCell>
            </TableRow>
            {gruposAbiertos["aprovisionamientos"] && (
              <>
                {/* Subgrupo: a) Consumo de mercaderías */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>a) Consumo de mercaderías</strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real = accData
                      .filter(
                        (a) =>
                          cuentasMercaderias.includes(a.codigo) &&
                          a.mes === parseInt(mes),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0);

                    const estimate = estimateData
                      .filter(
                        (e) =>
                          cuentasMercaderias.includes(e.codigo) &&
                          e.mes === parseInt(mes),
                      )
                      .reduce((acc, e) => acc + (e.valor || 0), 0);

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  {/* Totales */}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        cuentasMercaderias.reduce((acc, codigo) => {
                          return (
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasMercaderias.reduce((acc, codigo) => {
                          return (
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        cuentasMercaderias.reduce((acc, codigo) => {
                          return (
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasMercaderias.reduce((acc, codigo) => {
                          return (
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        cuentasMercaderias.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasMercaderias.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* Hijas de a) Consumo de mercaderías */}
                {cuentasMercaderias.map((codigo) => {
                  const lineaReal = accData.filter((a) => a.codigo === codigo);
                  const lineaEst = estimateData.filter(
                    (e) => e.codigo === codigo,
                  );

                  const valoresMesReal = {};
                  const valoresMesEst = {};
                  const valoresMesDiff = {};

                  let totalReal = 0;
                  let totalEst = 0;
                  let totalDiff = 0;

                  meses.forEach((mes) => {
                    const valorReal =
                      Number(
                        lineaReal.find((a) => a.mes === parseInt(mes))?.valor,
                      ) || 0;
                    const valorEst =
                      Number(
                        lineaEst.find((e) => e.mes === parseInt(mes))?.valor,
                      ) || 0;
                    const valorDiff = valorReal - valorEst;

                    valoresMesReal[mes] = valorReal;
                    valoresMesEst[mes] = valorEst;
                    valoresMesDiff[mes] = valorDiff;

                    totalReal += valorReal;
                    totalEst += valorEst;
                    totalDiff += valorDiff;
                  });

                  const titulo =
                    estructura.find((e) => e.codigo === codigo)?.titulo ||
                    codigo;

                  return (
                    <TableRow key={codigo}>
                      <TableCell
                        style={{
                          paddingLeft: "2em",
                          position: "sticky",
                          left: 0,
                          backgroundColor: ` ${theme.palette.background.paper}`,
                        }}
                      >
                        {titulo}
                      </TableCell>
                      {meses.map((mes) => (
                        <React.Fragment key={mes}>
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span
                              style={getEstiloValorEstimate(
                                valoresMesReal[mes],
                              )}
                            >
                              {formatearMiles(valoresMesReal[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesEst[mes])}>
                              {formatearMiles(valoresMesEst[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesDiff[mes])}>
                              {formatearMiles(valoresMesDiff[mes])}
                            </span>
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValorEstimate(totalReal)}>
                          {formatearMiles(totalReal)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalEst)}>
                          {formatearMiles(totalEst)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalDiff)}>
                          {formatearMiles(totalDiff)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}

            {gruposAbiertos["aprovisionamientos"] && (
              <>
                {/* Subgrupo: b) Consumo de materias primas */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>
                      b) Consumo de materias primas y otras materias
                      consumibles.
                    </strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real = accData
                      .filter(
                        (a) =>
                          cuentasMateriasPrimas.includes(a.codigo) &&
                          a.mes === parseInt(mes),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0);

                    const estimate = estimateData
                      .filter(
                        (e) =>
                          cuentasMateriasPrimas.includes(e.codigo) &&
                          e.mes === parseInt(mes),
                      )
                      .reduce((acc, e) => acc + (e.valor || 0), 0);

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        cuentasMateriasPrimas.reduce((acc, codigo) => {
                          return (
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasMateriasPrimas.reduce((acc, codigo) => {
                          return (
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        cuentasMateriasPrimas.reduce((acc, codigo) => {
                          return (
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasMateriasPrimas.reduce((acc, codigo) => {
                          return (
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        cuentasMateriasPrimas.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasMateriasPrimas.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* Hijas de b) Consumo de materias primas */}
                {cuentasMateriasPrimas.map((codigo) => {
                  const lineaReal = accData.filter((a) => a.codigo === codigo);
                  const lineaEst = estimateData.filter(
                    (e) => e.codigo === codigo,
                  );

                  const valoresMesReal = {};
                  const valoresMesEst = {};
                  const valoresMesDiff = {};

                  let totalReal = 0;
                  let totalEst = 0;
                  let totalDiff = 0;

                  meses.forEach((mes) => {
                    const valorReal =
                      Number(
                        lineaReal.find((a) => a.mes === parseInt(mes))?.valor,
                      ) || 0;
                    const valorEst =
                      Number(
                        lineaEst.find((e) => e.mes === parseInt(mes))?.valor,
                      ) || 0;
                    const valorDiff = valorReal - valorEst;

                    valoresMesReal[mes] = valorReal;
                    valoresMesEst[mes] = valorEst;
                    valoresMesDiff[mes] = valorDiff;

                    totalReal += valorReal;
                    totalEst += valorEst;
                    totalDiff += valorDiff;
                  });

                  const titulo =
                    estructura.find((e) => e.codigo === codigo)?.titulo ||
                    codigo;

                  return (
                    <TableRow key={codigo}>
                      <TableCell
                        style={{
                          paddingLeft: "2em",
                          position: "sticky",
                          left: 0,
                          backgroundColor: ` ${theme.palette.background.paper}`,
                        }}
                      >
                        {titulo}
                      </TableCell>
                      {meses.map((mes) => (
                        <React.Fragment key={mes}>
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span
                              style={getEstiloValorEstimate(
                                valoresMesReal[mes],
                              )}
                            >
                              {formatearMiles(valoresMesReal[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesEst[mes])}>
                              {formatearMiles(valoresMesEst[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesDiff[mes])}>
                              {formatearMiles(valoresMesDiff[mes])}
                            </span>
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValorEstimate(totalReal)}>
                          {formatearMiles(totalReal)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalEst)}>
                          {formatearMiles(totalEst)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalDiff)}>
                          {formatearMiles(totalDiff)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}

            {gruposAbiertos["aprovisionamientos"] && (
              <>
                {/* c) Trabajos realizados por otras empresas. */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>c) Trabajos realizados por otras empresas.</strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real = accData
                      .filter(
                        (a) =>
                          cuentasTrabajosExternos.includes(a.codigo) &&
                          a.mes === parseInt(mes),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0);

                    const estimate = estimateData
                      .filter(
                        (e) =>
                          cuentasTrabajosExternos.includes(e.codigo) &&
                          e.mes === parseInt(mes),
                      )
                      .reduce((acc, e) => acc + (e.valor || 0), 0);

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimateHead(
                        cuentasTrabajosExternos.reduce((acc, codigo) => {
                          return (
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasTrabajosExternos.reduce((acc, codigo) => {
                          return (
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        cuentasTrabajosExternos.reduce((acc, codigo) => {
                          return (
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasTrabajosExternos.reduce((acc, codigo) => {
                          return (
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        cuentasTrabajosExternos.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasTrabajosExternos.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* Hijas de c) Trabajos realizados por otras empresas */}
                {cuentasTrabajosExternos.map((codigo) => {
                  const lineaReal = accData.filter((a) => a.codigo === codigo);
                  const lineaEst = estimateData.filter(
                    (e) => e.codigo === codigo,
                  );

                  const valoresMesReal = {};
                  const valoresMesEst = {};
                  const valoresMesDiff = {};

                  let totalReal = 0;
                  let totalEst = 0;
                  let totalDiff = 0;

                  meses.forEach((mes) => {
                    const valorReal =
                      Number(
                        lineaReal.find((a) => a.mes === parseInt(mes))?.valor,
                      ) || 0;
                    const valorEst =
                      Number(
                        lineaEst.find((e) => e.mes === parseInt(mes))?.valor,
                      ) || 0;
                    const valorDiff = valorReal - valorEst;

                    valoresMesReal[mes] = valorReal;
                    valoresMesEst[mes] = valorEst;
                    valoresMesDiff[mes] = valorDiff;

                    totalReal += valorReal;
                    totalEst += valorEst;
                    totalDiff += valorDiff;
                  });

                  const titulo =
                    estructura.find((e) => e.codigo === codigo)?.titulo ||
                    codigo;

                  return (
                    <TableRow key={codigo}>
                      <TableCell
                        style={{
                          paddingLeft: "2em",
                          position: "sticky",
                          left: 0,
                          backgroundColor: ` ${theme.palette.background.paper}`,
                        }}
                      >
                        {titulo}
                      </TableCell>
                      {meses.map((mes) => (
                        <React.Fragment key={mes}>
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span
                              style={getEstiloValorEstimate(
                                valoresMesReal[mes],
                              )}
                            >
                              {formatearMiles(valoresMesReal[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesEst[mes])}>
                              {formatearMiles(valoresMesEst[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesDiff[mes])}>
                              {formatearMiles(valoresMesDiff[mes])}
                            </span>
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValorEstimate(totalReal)}>
                          {formatearMiles(totalReal)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalEst)}>
                          {formatearMiles(totalEst)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalDiff)}>
                          {formatearMiles(totalDiff)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}

            {/* d) Deterioro de mercaderías, materias primas y otros aprovisionamientos. */}
            <TableRow
              style={{ backgroundColor: ` ${theme.palette.estimates.main}` }}
            >
              <TableCell
                style={{
                  paddingLeft: "2em",
                  position: "sticky",
                  left: 0,
                  backgroundColor: ` ${theme.palette.estimates.main}`,
                }}
              >
                <strong>
                  d) Deterioro de mercaderías, materias primas y otros
                  aprovisionamientos.
                </strong>
              </TableCell>
              {meses.map((mes) => (
                <React.Fragment key={mes}>
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span style={getEstiloValorEstimateHead(0)}>
                      {(0).toLocaleString("es-ES", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={getEstiloValor1(0)}>
                      {(0).toLocaleString("es-ES", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={getEstiloValor1(0)}>
                      {(0).toLocaleString("es-ES", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </TableCell>
                </React.Fragment>
              ))}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span style={getEstiloValorEstimateHead(0)}>
                  {(0).toLocaleString("es-ES", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </TableCell>
              <TableCell align="right">
                <span style={getEstiloValor1(0)}>
                  {(0).toLocaleString("es-ES", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </TableCell>
              <TableCell align="right">
                <span style={getEstiloValor1(0)}>
                  {(0).toLocaleString("es-ES", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </TableCell>
            </TableRow>

            {/* 5. Otros ingresos de explotación */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("otrosIngresosExplotacion")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["otrosIngresosExplotacion"] ? "▼" : "►"} 5.
                  Otros ingresos de explotación
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = totalOtrosIngresosExplotacion[mes] || 0;
                const estimate =
                  getEstimateMeses(cuentasOtrosIngresos)[mes] || 0;
                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    totalAnualOtrosIngresosExplotacion,
                  )}
                >
                  {formatearMiles(totalAnualOtrosIngresosExplotacion)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    getEstimateMeses(cuentasOtrosIngresos).total,
                  )}
                >
                  {formatearMiles(getEstimateMeses(cuentasOtrosIngresos).total)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    totalAnualOtrosIngresosExplotacion -
                      getEstimateMeses(cuentasOtrosIngresos).total,
                  )}
                >
                  {formatearMiles(
                    totalAnualOtrosIngresosExplotacion -
                      getEstimateMeses(cuentasOtrosIngresos).total,
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* Subgrupo y cuentas hijas */}
            {gruposAbiertos["otrosIngresosExplotacion"] && (
              <>
                {/* a) Ingresos accesorios y otros de gestión corriente */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>
                      a) Ingresos accesorios y otros de gestión corriente
                    </strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real = accData
                      .filter(
                        (a) =>
                          cuentasOtrosIngresos.includes(a.codigo) &&
                          a.mes === parseInt(mes),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0);

                    const estimate = estimateData
                      .filter(
                        (e) =>
                          cuentasOtrosIngresos.includes(e.codigo) &&
                          e.mes === parseInt(mes),
                      )
                      .reduce((acc, e) => acc + (e.valor || 0), 0);

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  {/* Totales */}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimateHead(
                        cuentasOtrosIngresos.reduce(
                          (acc, codigo) =>
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0),
                          0,
                        ),
                      )}
                    >
                      {formatearMiles(
                        cuentasOtrosIngresos.reduce(
                          (acc, codigo) =>
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0),
                          0,
                        ),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        cuentasOtrosIngresos.reduce(
                          (acc, codigo) =>
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0),
                          0,
                        ),
                      )}
                    >
                      {formatearMiles(
                        cuentasOtrosIngresos.reduce(
                          (acc, codigo) =>
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0),
                          0,
                        ),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        cuentasOtrosIngresos.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasOtrosIngresos.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* Hijas de a) Ingresos accesorios */}
                {cuentasOtrosIngresos.map((codigo) => {
                  const lineaReal = accData.filter((a) => a.codigo === codigo);
                  const lineaEst = estimateData.filter(
                    (e) => e.codigo === codigo,
                  );

                  const valoresMesReal = {};
                  const valoresMesEst = {};
                  const valoresMesDiff = {};

                  let totalReal = 0;
                  let totalEst = 0;
                  let totalDiff = 0;

                  meses.forEach((mes) => {
                    const valorReal =
                      Number(
                        lineaReal.find((a) => a.mes === parseInt(mes))?.valor,
                      ) || 0;
                    const valorEst =
                      Number(
                        lineaEst.find((e) => e.mes === parseInt(mes))?.valor,
                      ) || 0;
                    const valorDiff = valorReal - valorEst;

                    valoresMesReal[mes] = valorReal;
                    valoresMesEst[mes] = valorEst;
                    valoresMesDiff[mes] = valorDiff;

                    totalReal += valorReal;
                    totalEst += valorEst;
                    totalDiff += valorDiff;
                  });

                  const titulo =
                    estructura.find((e) => e.codigo === codigo)?.titulo ||
                    codigo;

                  return (
                    <TableRow key={codigo}>
                      <TableCell
                        style={{
                          paddingLeft: "2em",
                          position: "sticky",
                          left: 0,
                          backgroundColor: ` ${theme.palette.background.paper}`,
                        }}
                      >
                        {titulo}
                      </TableCell>
                      {meses.map((mes) => (
                        <>
                          <TableCell
                            key={`real-${codigo}-${mes}`}
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span
                              style={getEstiloValorEstimate(
                                valoresMesReal[mes],
                              )}
                            >
                              {formatearMiles(valoresMesReal[mes])}
                            </span>
                          </TableCell>
                          <TableCell key={`est-${codigo}-${mes}`} align="right">
                            <span style={getEstiloValor(valoresMesEst[mes])}>
                              {formatearMiles(valoresMesEst[mes])}
                            </span>
                          </TableCell>
                          <TableCell
                            key={`diff-${codigo}-${mes}`}
                            align="right"
                          >
                            <span style={getEstiloValor(valoresMesDiff[mes])}>
                              {formatearMiles(valoresMesDiff[mes])}
                            </span>
                          </TableCell>
                        </>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValorEstimate(totalReal)}>
                          {formatearMiles(totalReal)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalEst)}>
                          {formatearMiles(totalEst)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalDiff)}>
                          {formatearMiles(totalDiff)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}

            {/* b) Subvenciones de explotación incorporadas al resultado del ejercicio */}
            <TableRow
              style={{ backgroundColor: ` ${theme.palette.estimates.main}` }}
            >
              <TableCell
                style={{
                  paddingLeft: "2em",
                  position: "sticky",
                  left: 0,
                  backgroundColor: ` ${theme.palette.estimates.main}`,
                }}
              >
                <strong>
                  b) Subvenciones de explotación incorporadas al resultado del
                  ejercicio
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = accData
                  .filter(
                    (a) => a.codigo === "74600000" && a.mes === parseInt(mes),
                  )
                  .reduce((acc, a) => acc + (a.valor || 0), 0);

                const estimate = estimateData
                  .filter(
                    (e) => e.codigo === "74600000" && e.mes === parseInt(mes),
                  )
                  .reduce((acc, e) => acc + (e.valor || 0), 0);

                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    accData
                      .filter((a) => a.codigo === "74600000")
                      .reduce((sum, a) => sum + (a.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    accData
                      .filter((a) => a.codigo === "74600000")
                      .reduce((sum, a) => sum + (a.valor || 0), 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    estimateData
                      .filter((e) => e.codigo === "74600000")
                      .reduce((sum, e) => sum + (e.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    estimateData
                      .filter((e) => e.codigo === "74600000")
                      .reduce((sum, e) => sum + (e.valor || 0), 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    accData
                      .filter((a) => a.codigo === "74600000")
                      .reduce((sum, a) => sum + (a.valor || 0), 0) -
                      estimateData
                        .filter((e) => e.codigo === "74600000")
                        .reduce((sum, e) => sum + (e.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    accData
                      .filter((a) => a.codigo === "74600000")
                      .reduce((sum, a) => sum + (a.valor || 0), 0) -
                      estimateData
                        .filter((e) => e.codigo === "74600000")
                        .reduce((sum, e) => sum + (e.valor || 0), 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* 6. Gastos de personal */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("gastosPersonal")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["gastosPersonal"] ? "▼" : "►"} 6. Gastos de
                  personal
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = resultadoGastosPersonal[mes] || 0;
                const estimate =
                  getEstimateMeses([
                    ...cuentasSueldos,
                    ...cuentasCargasSociales,
                    ...cuentasProvisiones,
                  ])[mes] || 0;
                const diff = real - estimate;
                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    resultadoGastosPersonal.total,
                  )}
                >
                  {formatearMiles(resultadoGastosPersonal.total)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    getEstimateMeses([
                      ...cuentasSueldos,
                      ...cuentasCargasSociales,
                      ...cuentasProvisiones,
                    ]).total,
                  )}
                >
                  {formatearMiles(
                    getEstimateMeses([
                      ...cuentasSueldos,
                      ...cuentasCargasSociales,
                      ...cuentasProvisiones,
                    ]).total,
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    resultadoGastosPersonal.total -
                      estimateGastosPersonal.total,
                  )}
                >
                  {formatearMiles(
                    resultadoGastosPersonal.total -
                      estimateGastosPersonal.total,
                  )}
                </span>
              </TableCell>
            </TableRow>
            {/* Subgrupos y cuentas hijas */}
            {gruposAbiertos["gastosPersonal"] && (
              <>
                {/* a) Sueldos, salarios y asimilados */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>a) Sueldos, salarios y asimilados</strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real = accData
                      .filter(
                        (a) =>
                          cuentasSueldos.includes(a.codigo) &&
                          a.mes === parseInt(mes),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0);

                    const estimate = estimateData
                      .filter(
                        (e) =>
                          cuentasSueldos.includes(e.codigo) &&
                          e.mes === parseInt(mes),
                      )
                      .reduce((acc, e) => acc + (e.valor || 0), 0);

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  {/* Totales */}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimateHead(
                        cuentasSueldos.reduce((acc, codigo) => {
                          return (
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasSueldos.reduce((acc, codigo) => {
                          return (
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        cuentasSueldos.reduce((acc, codigo) => {
                          return (
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasSueldos.reduce((acc, codigo) => {
                          return (
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        cuentasSueldos.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasSueldos.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* Hijas de a) Sueldos, salarios y asimilados */}
                {cuentasSueldos.map((codigo) => {
                  const lineaReal = accData.filter((a) => a.codigo === codigo);
                  const lineaEst = estimateData.filter(
                    (e) => e.codigo === codigo,
                  );

                  const valoresMesReal = {};
                  const valoresMesEst = {};
                  const valoresMesDiff = {};

                  let totalReal = 0;
                  let totalEst = 0;

                  meses.forEach((mes) => {
                    const real =
                      lineaReal.find((a) => a.mes === parseInt(mes))?.valor ||
                      0;
                    const est =
                      lineaEst.find((e) => e.mes === parseInt(mes))?.valor || 0;

                    valoresMesReal[mes] = real;
                    valoresMesEst[mes] = est;
                    valoresMesDiff[mes] = real - est;

                    totalReal += real;
                    totalEst += est;
                  });

                  const titulo =
                    estructura.find((e) => e.codigo === codigo)?.titulo ||
                    titulosCuentaPorCodigo[codigo] ||
                    codigo;

                  return (
                    <TableRow key={codigo}>
                      <TableCell
                        style={{
                          paddingLeft: "2em",
                          position: "sticky",
                          left: 0,
                          backgroundColor: ` ${theme.palette.background.paper}`,
                        }}
                      >
                        {titulo}
                      </TableCell>
                      {meses.map((mes) => (
                        <React.Fragment key={mes}>
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span
                              style={getEstiloValorEstimate(
                                valoresMesReal[mes],
                              )}
                            >
                              {formatearMiles(valoresMesReal[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesEst[mes])}>
                              {formatearMiles(valoresMesEst[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesDiff[mes])}>
                              {formatearMiles(valoresMesDiff[mes])}
                            </span>
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValorEstimate(totalReal)}>
                          {formatearMiles(totalReal)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalEst)}>
                          {formatearMiles(totalEst)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalReal - totalEst)}>
                          {formatearMiles(totalReal - totalEst)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* b) Cargas sociales */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>b) Cargas sociales</strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real = accData
                      .filter(
                        (a) =>
                          cuentasCargasSociales.includes(a.codigo) &&
                          a.mes === parseInt(mes),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0);

                    const estimate = estimateData
                      .filter(
                        (e) =>
                          cuentasCargasSociales.includes(e.codigo) &&
                          e.mes === parseInt(mes),
                      )
                      .reduce((acc, e) => acc + (e.valor || 0), 0);

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}

                  {/* TOTAL */}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimateHead(
                        cuentasCargasSociales.reduce((acc, codigo) => {
                          return (
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasCargasSociales.reduce((acc, codigo) => {
                          return (
                            acc +
                            accData
                              .filter((a) => a.codigo === codigo)
                              .reduce((sum, a) => sum + (a.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        cuentasCargasSociales.reduce((acc, codigo) => {
                          return (
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasCargasSociales.reduce((acc, codigo) => {
                          return (
                            acc +
                            estimateData
                              .filter((e) => e.codigo === codigo)
                              .reduce((sum, e) => sum + (e.valor || 0), 0)
                          );
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        cuentasCargasSociales.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    >
                      {formatearMiles(
                        cuentasCargasSociales.reduce((acc, codigo) => {
                          const real = accData
                            .filter((a) => a.codigo === codigo)
                            .reduce((sum, a) => sum + (a.valor || 0), 0);
                          const est = estimateData
                            .filter((e) => e.codigo === codigo)
                            .reduce((sum, e) => sum + (e.valor || 0), 0);
                          return acc + (real - est);
                        }, 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* Hijas de b) Cargas sociales */}
                {cuentasCargasSociales.map((codigo) => {
                  const lineaReal = accData.filter((a) => a.codigo === codigo);
                  const lineaEst = estimateData.filter(
                    (e) => e.codigo === codigo,
                  );

                  const valoresMesReal = {};
                  const valoresMesEst = {};
                  const valoresMesDiff = {};

                  let totalReal = 0;
                  let totalEst = 0;

                  meses.forEach((mes) => {
                    const real =
                      lineaReal.find((a) => a.mes === parseInt(mes))?.valor ||
                      0;
                    const est =
                      lineaEst.find((e) => e.mes === parseInt(mes))?.valor || 0;

                    valoresMesReal[mes] = real;
                    valoresMesEst[mes] = est;
                    valoresMesDiff[mes] = real - est;

                    totalReal += real;
                    totalEst += est;
                  });

                  const titulo =
                    estructura.find((e) => e.codigo === codigo)?.titulo ||
                    codigo;

                  return (
                    <TableRow key={codigo}>
                      <TableCell
                        style={{
                          paddingLeft: "2em",
                          position: "sticky",
                          left: 0,
                          backgroundColor: ` ${theme.palette.background.paper}`,
                        }}
                      >
                        {titulo}
                      </TableCell>
                      {meses.map((mes) => (
                        <React.Fragment key={mes}>
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span
                              style={getEstiloValorEstimate(
                                valoresMesReal[mes],
                              )}
                            >
                              {formatearMiles(valoresMesReal[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesEst[mes])}>
                              {formatearMiles(valoresMesEst[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesDiff[mes])}>
                              {formatearMiles(valoresMesDiff[mes])}
                            </span>
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValorEstimate(totalReal)}>
                          {formatearMiles(totalReal)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalEst)}>
                          {formatearMiles(totalEst)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalReal - totalEst)}>
                          {formatearMiles(totalReal - totalEst)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {cuentasProvisiones.length > 0 ? (
                  <>
                    {/* c) Provisiones */}
                    <TableRow
                      style={{
                        backgroundColor: ` ${theme.palette.estimates.main}`,
                      }}
                    >
                      <TableCell
                        style={{
                          paddingLeft: "2em",
                          position: "sticky",
                          left: 0,
                          backgroundColor: ` ${theme.palette.estimates.main}`,
                        }}
                      >
                        <strong>c) Provisiones</strong>
                      </TableCell>
                      {meses.map((mes) => {
                        const real = accData
                          .filter(
                            (a) =>
                              cuentasProvisiones.includes(a.codigo) &&
                              a.mes === parseInt(mes),
                          )
                          .reduce((acc, a) => acc + (a.valor || 0), 0);

                        const estimate = estimateData
                          .filter(
                            (e) =>
                              cuentasProvisiones.includes(e.codigo) &&
                              e.mes === parseInt(mes),
                          )
                          .reduce((acc, e) => acc + (e.valor || 0), 0);

                        const diff = real - estimate;

                        return (
                          <React.Fragment key={mes}>
                            <TableCell
                              align="right"
                              sx={{
                                borderLeft: `1px solid ${theme.palette.pyg}`,
                              }}
                            >
                              <span style={getEstiloValorEstimateHead(real)}>
                                {formatearMiles(real)}
                              </span>
                            </TableCell>
                            <TableCell align="right">
                              <span style={getEstiloValor1(estimate)}>
                                {formatearMiles(estimate)}
                              </span>
                            </TableCell>
                            <TableCell align="right">
                              <span style={getEstiloValor1(diff)}>
                                {formatearMiles(diff)}
                              </span>
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                      {/* Totales */}
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span
                          style={getEstiloValorEstimateHead(
                            cuentasProvisiones.reduce((acc, codigo) => {
                              return (
                                acc +
                                accData
                                  .filter((a) => a.codigo === codigo)
                                  .reduce((sum, a) => sum + (a.valor || 0), 0)
                              );
                            }, 0),
                          )}
                        >
                          {formatearMiles(
                            cuentasProvisiones.reduce((acc, codigo) => {
                              return (
                                acc +
                                accData
                                  .filter((a) => a.codigo === codigo)
                                  .reduce((sum, a) => sum + (a.valor || 0), 0)
                              );
                            }, 0),
                          )}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span
                          style={getEstiloValor1(
                            cuentasProvisiones.reduce((acc, codigo) => {
                              return (
                                acc +
                                estimateData
                                  .filter((e) => e.codigo === codigo)
                                  .reduce((sum, e) => sum + (e.valor || 0), 0)
                              );
                            }, 0),
                          )}
                        >
                          {formatearMiles(
                            cuentasProvisiones.reduce((acc, codigo) => {
                              return (
                                acc +
                                estimateData
                                  .filter((e) => e.codigo === codigo)
                                  .reduce((sum, e) => sum + (e.valor || 0), 0)
                              );
                            }, 0),
                          )}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span
                          style={getEstiloValor1(
                            cuentasProvisiones.reduce((acc, codigo) => {
                              const real = accData
                                .filter((a) => a.codigo === codigo)
                                .reduce((sum, a) => sum + (a.valor || 0), 0);
                              const est = estimateData
                                .filter((e) => e.codigo === codigo)
                                .reduce((sum, e) => sum + (e.valor || 0), 0);
                              return acc + (real - est);
                            }, 0),
                          )}
                        >
                          {formatearMiles(
                            cuentasProvisiones.reduce((acc, codigo) => {
                              const real = accData
                                .filter((a) => a.codigo === codigo)
                                .reduce((sum, a) => sum + (a.valor || 0), 0);
                              const est = estimateData
                                .filter((e) => e.codigo === codigo)
                                .reduce((sum, e) => sum + (e.valor || 0), 0);
                              return acc + (real - est);
                            }, 0),
                          )}
                        </span>
                      </TableCell>
                    </TableRow>

                    {/* Hijas de c) Provisiones */}
                    {cuentasProvisiones.map((codigo) => {
                      const lineaReal = accData.filter(
                        (a) => a.codigo === codigo,
                      );
                      const lineaEst = estimateData.filter(
                        (e) => e.codigo === codigo,
                      );

                      const valoresMesReal = {};
                      const valoresMesEst = {};
                      const valoresMesDiff = {};

                      let totalReal = 0;
                      let totalEst = 0;
                      let totalDiff = 0;

                      meses.forEach((mes) => {
                        const valorReal =
                          Number(
                            lineaReal.find((a) => a.mes === parseInt(mes))
                              ?.valor,
                          ) || 0;
                        const valorEst =
                          Number(
                            lineaEst.find((e) => e.mes === parseInt(mes))
                              ?.valor,
                          ) || 0;
                        const valorDiff = valorReal - valorEst;

                        valoresMesReal[mes] = valorReal;
                        valoresMesEst[mes] = valorEst;
                        valoresMesDiff[mes] = valorDiff;

                        totalReal += valorReal;
                        totalEst += valorEst;
                        totalDiff += valorDiff;
                      });

                      const titulo =
                        estructura.find((e) => e.codigo === codigo)?.titulo ||
                        titulosCuentaPorCodigo[codigo] ||
                        codigo;

                      return (
                        <TableRow key={codigo}>
                          <TableCell
                            style={{
                              paddingLeft: "2em",
                              position: "sticky",
                              left: 0,
                              backgroundColor: ` ${theme.palette.background.paper}`,
                            }}
                          >
                            {titulo}
                          </TableCell>
                          {meses.map((mes) => (
                            <React.Fragment key={mes}>
                              <TableCell
                                align="right"
                                sx={{
                                  borderLeft: `1px solid ${theme.palette.pyg}`,
                                }}
                              >
                                <span
                                  style={getEstiloValorEstimate(
                                    valoresMesReal[mes],
                                  )}
                                >
                                  {formatearMiles(valoresMesReal[mes])}
                                </span>
                              </TableCell>
                              <TableCell align="right">
                                <span
                                  style={getEstiloValor(valoresMesEst[mes])}
                                >
                                  {formatearMiles(valoresMesEst[mes])}
                                </span>
                              </TableCell>
                              <TableCell align="right">
                                <span
                                  style={getEstiloValor(valoresMesDiff[mes])}
                                >
                                  {formatearMiles(valoresMesDiff[mes])}
                                </span>
                              </TableCell>
                            </React.Fragment>
                          ))}
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span style={getEstiloValorEstimate(totalReal)}>
                              {formatearMiles(totalReal)}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(totalEst)}>
                              {formatearMiles(totalEst)}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(totalDiff)}>
                              {formatearMiles(totalDiff)}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                ) : null}
              </>
            )}

            {/* 7. Otros gastos de explotación */}
            <TableRow
              style={{
                backgroundColor: `${theme.palette.pyg1}`,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("otrosGastosExplotacion")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: `${theme.palette.pyg1}`,
                }}
              >
                <strong>
                  {gruposAbiertos["otrosGastosExplotacion"] ? "▼" : "►"} 7.
                  Otros gastos de explotación
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = resultadoOtrosGastosExplotacion[mes] || 0;
                const estimate = estimateOtrosGastosExplotacion[mes] || 0;
                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    resultadoOtrosGastosExplotacion.total,
                  )}
                >
                  {formatearMiles(resultadoOtrosGastosExplotacion.total)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(estimateOtrosGastosExplotacion.total)}
                >
                  {formatearMiles(estimateOtrosGastosExplotacion.total)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    resultadoOtrosGastosExplotacion.total -
                      estimateOtrosGastosExplotacion.total,
                  )}
                >
                  {formatearMiles(
                    resultadoOtrosGastosExplotacion.total -
                      estimateOtrosGastosExplotacion.total,
                  )}
                </span>
              </TableCell>
            </TableRow>

            {gruposAbiertos["otrosGastosExplotacion"] && (
              <>
                {/* a) Servicios exteriores. */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>a) Servicios exteriores</strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real = accData
                      .filter(
                        (a) =>
                          cuentasServiciosExteriores.includes(a.codigo) &&
                          a.mes === parseInt(mes),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0);

                    const estimate = estimateData
                      .filter(
                        (e) =>
                          cuentasServiciosExteriores.includes(e.codigo) &&
                          e.mes === parseInt(mes),
                      )
                      .reduce((acc, e) => acc + (e.valor || 0), 0);

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimateHead(
                        accData
                          .filter((a) =>
                            cuentasServiciosExteriores.includes(a.codigo),
                          )
                          .reduce((acc, a) => acc + (a.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        accData
                          .filter((a) =>
                            cuentasServiciosExteriores.includes(a.codigo),
                          )
                          .reduce((acc, a) => acc + (a.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        estimateData
                          .filter((e) =>
                            cuentasServiciosExteriores.includes(e.codigo),
                          )
                          .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        estimateData
                          .filter((e) =>
                            cuentasServiciosExteriores.includes(e.codigo),
                          )
                          .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        accData
                          .filter((a) =>
                            cuentasServiciosExteriores.includes(a.codigo),
                          )
                          .reduce((acc, a) => acc + (a.valor || 0), 0) -
                          estimateData
                            .filter((e) =>
                              cuentasServiciosExteriores.includes(e.codigo),
                            )
                            .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        accData
                          .filter((a) =>
                            cuentasServiciosExteriores.includes(a.codigo),
                          )
                          .reduce((acc, a) => acc + (a.valor || 0), 0) -
                          estimateData
                            .filter((e) =>
                              cuentasServiciosExteriores.includes(e.codigo),
                            )
                            .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* Hijas de a) Servicios exteriores */}
                {cuentasServiciosExteriores.map((codigo) => {
                  const lineaReal = accData.filter((a) => a.codigo === codigo);
                  const lineaEst = estimateData.filter(
                    (e) => e.codigo === codigo,
                  );

                  const valoresMesReal = {};
                  const valoresMesEst = {};
                  const valoresMesDiff = {};

                  let totalReal = 0;
                  let totalEst = 0;

                  meses.forEach((mes) => {
                    const valorReal =
                      Number(
                        lineaReal.find((a) => a.mes === parseInt(mes))?.valor,
                      ) || 0;
                    const valorEst =
                      Number(
                        lineaEst.find((e) => e.mes === parseInt(mes))?.valor,
                      ) || 0;

                    valoresMesReal[mes] = valorReal;
                    valoresMesEst[mes] = valorEst;
                    valoresMesDiff[mes] = valorReal - valorEst;

                    totalReal += valorReal;
                    totalEst += valorEst;
                  });

                  const titulo =
                    estructura.find((e) => e.codigo === codigo)?.titulo ||
                    titulosCuentaPorCodigo[codigo] ||
                    codigo;

                  return (
                    <TableRow key={codigo}>
                      <TableCell
                        style={{
                          paddingLeft: "2em",
                          position: "sticky",
                          left: 0,
                          backgroundColor: ` ${theme.palette.background.paper}`,
                        }}
                      >
                        {titulo}
                      </TableCell>
                      {meses.map((mes) => (
                        <React.Fragment key={mes}>
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span
                              style={getEstiloValorEstimate(
                                valoresMesReal[mes],
                              )}
                            >
                              {formatearMiles(valoresMesReal[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesEst[mes])}>
                              {formatearMiles(valoresMesEst[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesDiff[mes])}>
                              {formatearMiles(valoresMesDiff[mes])}
                            </span>
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValorEstimate(totalReal)}>
                          {formatearMiles(totalReal)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalEst)}>
                          {formatearMiles(totalEst)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalReal - totalEst)}>
                          {formatearMiles(totalReal - totalEst)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* b) Tributos. */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>b) Tributos</strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real = accData
                      .filter(
                        (a) =>
                          cuentasTributos.includes(a.codigo) &&
                          a.mes === parseInt(mes),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0);

                    const estimate = estimateData
                      .filter(
                        (e) =>
                          cuentasTributos.includes(e.codigo) &&
                          e.mes === parseInt(mes),
                      )
                      .reduce((acc, e) => acc + (e.valor || 0), 0);

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}

                  {/* Totales finales */}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimateHead(
                        accData
                          .filter((a) => cuentasTributos.includes(a.codigo))
                          .reduce((acc, a) => acc + (a.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        accData
                          .filter((a) => cuentasTributos.includes(a.codigo))
                          .reduce((acc, a) => acc + (a.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        estimateData
                          .filter((e) => cuentasTributos.includes(e.codigo))
                          .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        estimateData
                          .filter((e) => cuentasTributos.includes(e.codigo))
                          .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        accData
                          .filter((a) => cuentasTributos.includes(a.codigo))
                          .reduce((acc, a) => acc + (a.valor || 0), 0) -
                          estimateData
                            .filter((e) => cuentasTributos.includes(e.codigo))
                            .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        accData
                          .filter((a) => cuentasTributos.includes(a.codigo))
                          .reduce((acc, a) => acc + (a.valor || 0), 0) -
                          estimateData
                            .filter((e) => cuentasTributos.includes(e.codigo))
                            .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* Hijas de b) Tributos */}
                {cuentasTributos.map((codigo) => {
                  const lineaReal = accData.filter((a) => a.codigo === codigo);
                  const lineaEst = estimateData.filter(
                    (e) => e.codigo === codigo,
                  );

                  const valoresMesReal = {};
                  const valoresMesEst = {};
                  const valoresMesDiff = {};

                  let totalReal = 0;
                  let totalEst = 0;

                  meses.forEach((mes) => {
                    const valorReal =
                      Number(
                        lineaReal.find((a) => a.mes === parseInt(mes))?.valor,
                      ) || 0;
                    const valorEst =
                      Number(
                        lineaEst.find((e) => e.mes === parseInt(mes))?.valor,
                      ) || 0;

                    valoresMesReal[mes] = valorReal;
                    valoresMesEst[mes] = valorEst;
                    valoresMesDiff[mes] = valorReal - valorEst;

                    totalReal += valorReal;
                    totalEst += valorEst;
                  });

                  const titulo =
                    estructura.find((e) => e.codigo === codigo)?.titulo ||
                    codigo;

                  return (
                    <TableRow key={codigo}>
                      <TableCell
                        style={{
                          paddingLeft: "2em",
                          position: "sticky",
                          left: 0,
                          backgroundColor: ` ${theme.palette.background.paper}`,
                        }}
                      >
                        {titulo}
                      </TableCell>
                      {meses.map((mes) => (
                        <React.Fragment key={mes}>
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span
                              style={getEstiloValorEstimate(
                                valoresMesReal[mes],
                              )}
                            >
                              {formatearMiles(valoresMesReal[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesEst[mes])}>
                              {formatearMiles(valoresMesEst[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesDiff[mes])}>
                              {formatearMiles(valoresMesDiff[mes])}
                            </span>
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValorEstimate(totalReal)}>
                          {formatearMiles(totalReal)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalEst)}>
                          {formatearMiles(totalEst)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalReal - totalEst)}>
                          {formatearMiles(totalReal - totalEst)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}

            {/* c) Pérdidas, deterioro y variación de provisiones por operaciones comerciales. */}
            <TableRow
              style={{ backgroundColor: ` ${theme.palette.estimates.main}` }}
            >
              <TableCell
                style={{
                  paddingLeft: "2em",
                  position: "sticky",
                  left: 0,
                  backgroundColor: ` ${theme.palette.estimates.main}`,
                }}
              >
                <strong>
                  c) Pérdidas, deterioro y variación de provisiones por
                  operaciones comerciales
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = resultadoProvisionesComerciales[mes] || 0;
                const estimate = estimateProvisionesComerciales[mes] || 0;
                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    resultadoProvisionesComerciales.total,
                  )}
                >
                  {formatearMiles(resultadoProvisionesComerciales.total)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(estimateProvisionesComerciales.total)}
                >
                  {formatearMiles(estimateProvisionesComerciales.total)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    resultadoProvisionesComerciales.total -
                      estimateProvisionesComerciales.total,
                  )}
                >
                  {formatearMiles(
                    resultadoProvisionesComerciales.total -
                      estimateProvisionesComerciales.total,
                  )}
                </span>
              </TableCell>
            </TableRow>

            {cuentasProvisionesComerciales.map((codigo) => {
              const lineaReal = accData.filter((a) => a.codigo === codigo);
              const lineaEst = estimateData.filter((e) => e.codigo === codigo);
              const titulo =
                titulosCuentaPorCodigo[codigo] ||
                lineaReal[0]?.titulo ||
                codigo;

              const valoresMesReal = {};
              const valoresMesEst = {};
              const valoresMesDiff = {};
              let totalReal = 0;
              let totalEst = 0;
              let totalDiff = 0;

              meses.forEach((mes) => {
                const valorReal =
                  Number(
                    lineaReal.find((a) => a.mes === parseInt(mes))?.valor,
                  ) || 0;
                const valorEst =
                  Number(
                    lineaEst.find((e) => e.mes === parseInt(mes))?.valor,
                  ) || 0;
                valoresMesReal[mes] = valorReal;
                valoresMesEst[mes] = valorEst;
                valoresMesDiff[mes] = valorReal - valorEst;
                totalReal += valorReal;
                totalEst += valorEst;
                totalDiff += valorReal - valorEst;
              });

              return (
                <TableRow key={codigo}>
                  <TableCell
                    style={{
                      paddingLeft: "4em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    {titulo}
                  </TableCell>
                  {meses.map((mes) => (
                    <React.Fragment key={mes}>
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span
                          style={getEstiloValorEstimate(valoresMesReal[mes])}
                        >
                          {formatearMiles(valoresMesReal[mes])}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(valoresMesEst[mes])}>
                          {formatearMiles(valoresMesEst[mes])}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(valoresMesDiff[mes])}>
                          {formatearMiles(valoresMesDiff[mes])}
                        </span>
                      </TableCell>
                    </React.Fragment>
                  ))}
                  <TableCell
                    align="right"
                    sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
                  >
                    <span style={getEstiloValorEstimate(totalReal)}>
                      {formatearMiles(totalReal)}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={getEstiloValor(totalEst)}>
                      {formatearMiles(totalEst)}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={getEstiloValor(totalDiff)}>
                      {formatearMiles(totalDiff)}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* d) Otros gastos de gestión corriente */}
            {gruposAbiertos["otrosGastosExplotacion"] && (
              <>
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>d) Otros gastos de gestión corriente</strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real = accData
                      .filter(
                        (a) =>
                          cuentasOtrosGastosGestion.includes(a.codigo) &&
                          a.mes === parseInt(mes),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0);

                    const estimate = estimateData
                      .filter(
                        (e) =>
                          cuentasOtrosGastosGestion.includes(e.codigo) &&
                          e.mes === parseInt(mes),
                      )
                      .reduce((acc, e) => acc + (e.valor || 0), 0);

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimateHead(
                        accData
                          .filter((a) =>
                            cuentasOtrosGastosGestion.includes(a.codigo),
                          )
                          .reduce((acc, a) => acc + (a.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        accData
                          .filter((a) =>
                            cuentasOtrosGastosGestion.includes(a.codigo),
                          )
                          .reduce((acc, a) => acc + (a.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        estimateData
                          .filter((e) =>
                            cuentasOtrosGastosGestion.includes(e.codigo),
                          )
                          .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        estimateData
                          .filter((e) =>
                            cuentasOtrosGastosGestion.includes(e.codigo),
                          )
                          .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        accData
                          .filter((a) =>
                            cuentasOtrosGastosGestion.includes(a.codigo),
                          )
                          .reduce((acc, a) => acc + (a.valor || 0), 0) -
                          estimateData
                            .filter((e) =>
                              cuentasOtrosGastosGestion.includes(e.codigo),
                            )
                            .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        accData
                          .filter((a) =>
                            cuentasOtrosGastosGestion.includes(a.codigo),
                          )
                          .reduce((acc, a) => acc + (a.valor || 0), 0) -
                          estimateData
                            .filter((e) =>
                              cuentasOtrosGastosGestion.includes(e.codigo),
                            )
                            .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* Hijas de d) Otros gastos de gestión corriente */}
                {cuentasOtrosGastosGestion.map((codigo) => {
                  const lineaReal = accData.filter((a) => a.codigo === codigo);
                  const lineaEst = estimateData.filter(
                    (e) => e.codigo === codigo,
                  );

                  const valoresMesReal = {};
                  const valoresMesEst = {};
                  const valoresMesDiff = {};

                  let totalReal = 0;
                  let totalEst = 0;

                  meses.forEach((mes) => {
                    const valorReal =
                      lineaReal.find((a) => a.mes === parseInt(mes))?.valor ||
                      0;
                    const valorEst =
                      lineaEst.find((e) => e.mes === parseInt(mes))?.valor || 0;
                    const valorDiff = valorReal - valorEst;

                    valoresMesReal[mes] = valorReal;
                    valoresMesEst[mes] = valorEst;
                    valoresMesDiff[mes] = valorDiff;

                    totalReal += valorReal;
                    totalEst += valorEst;
                  });

                  const totalDiff = totalReal - totalEst;

                  const titulo =
                    estructura.find((e) => e.codigo === codigo)?.titulo ||
                    codigo;

                  return (
                    <TableRow key={codigo}>
                      <TableCell
                        style={{
                          paddingLeft: "2em",
                          position: "sticky",
                          left: 0,
                          backgroundColor: ` ${theme.palette.background.paper}`,
                        }}
                      >
                        {titulo}
                      </TableCell>
                      {meses.map((mes) => (
                        <React.Fragment key={`${codigo}-${mes}`}>
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span
                              style={getEstiloValorEstimate(
                                valoresMesReal[mes],
                              )}
                            >
                              {formatearMiles(valoresMesReal[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesEst[mes])}>
                              {formatearMiles(valoresMesEst[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesDiff[mes])}>
                              {formatearMiles(valoresMesDiff[mes])}
                            </span>
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          borderLeft: `1px solid ${theme.palette.pyg}`,
                        }}
                      >
                        <span style={getEstiloValorEstimate(totalReal)}>
                          {formatearMiles(totalReal)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalEst)}>
                          {formatearMiles(totalEst)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={getEstiloValor(totalDiff)}>
                          {formatearMiles(totalDiff)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}

            {/* 8. Amortización del inmovilizado */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("amortizacion")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["amortizacion"] ? "▼" : "►"} 8. Amortización
                  del inmovilizado
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = accData
                  .filter(
                    (a) =>
                      cuentasAmortizacion.includes(a.codigo) &&
                      a.mes === parseInt(mes),
                  )
                  .reduce((acc, a) => acc + (a.valor || 0), 0);

                const estimate = estimateData
                  .filter(
                    (e) =>
                      cuentasAmortizacion.includes(e.codigo) &&
                      e.mes === parseInt(mes),
                  )
                  .reduce((acc, e) => acc + (e.valor || 0), 0);

                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}

              {/* Total anual */}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    accData
                      .filter((a) => cuentasAmortizacion.includes(a.codigo))
                      .reduce((acc, a) => acc + (a.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    accData
                      .filter((a) => cuentasAmortizacion.includes(a.codigo))
                      .reduce((acc, a) => acc + (a.valor || 0), 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    estimateData
                      .filter((e) => cuentasAmortizacion.includes(e.codigo))
                      .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    estimateData
                      .filter((e) => cuentasAmortizacion.includes(e.codigo))
                      .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    accData
                      .filter((a) => cuentasAmortizacion.includes(a.codigo))
                      .reduce((acc, a) => acc + (a.valor || 0), 0) -
                      estimateData
                        .filter((e) => cuentasAmortizacion.includes(e.codigo))
                        .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    accData
                      .filter((a) => cuentasAmortizacion.includes(a.codigo))
                      .reduce((acc, a) => acc + (a.valor || 0), 0) -
                      estimateData
                        .filter((e) => cuentasAmortizacion.includes(e.codigo))
                        .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* Hijas de 8. Amortización */}
            {gruposAbiertos["amortizacion"] &&
              cuentasAmortizacion.map((codigo) => {
                const lineaReal = accData.filter((a) => a.codigo === codigo);
                const lineaEst = estimateData.filter(
                  (e) => e.codigo === codigo,
                );

                const valoresMesReal = {};
                const valoresMesEst = {};
                const valoresMesDiff = {};

                let totalReal = 0;
                let totalEst = 0;

                meses.forEach((mes) => {
                  const valorReal =
                    lineaReal.find((a) => a.mes === parseInt(mes))?.valor || 0;
                  const valorEst =
                    lineaEst.find((e) => e.mes === parseInt(mes))?.valor || 0;

                  valoresMesReal[mes] = valorReal;
                  valoresMesEst[mes] = valorEst;
                  valoresMesDiff[mes] = valorReal - valorEst;

                  totalReal += valorReal;
                  totalEst += valorEst;
                });

                const titulo =
                  estructura.find((e) => e.codigo === codigo)?.titulo || codigo;

                return (
                  <TableRow key={codigo}>
                    <TableCell
                      style={{
                        paddingLeft: "2em",
                        position: "sticky",
                        left: 0,
                        backgroundColor: ` ${theme.palette.background.paper}`,
                      }}
                    >
                      {titulo}
                    </TableCell>
                    {meses.map((mes) => (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span
                            style={getEstiloValorEstimate(valoresMesReal[mes])}
                          >
                            {formatearMiles(valoresMesReal[mes])}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(valoresMesEst[mes])}>
                            {formatearMiles(valoresMesEst[mes])}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(valoresMesDiff[mes])}>
                            {formatearMiles(valoresMesDiff[mes])}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    ))}
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimate(totalReal)}>
                        {formatearMiles(totalReal)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor(totalEst)}>
                        {formatearMiles(totalEst)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor(totalReal - totalEst)}>
                        {formatearMiles(totalReal - totalEst)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}

            {/* 9. Imputación de subvenciones de inmovilizado no financiero y otras */}
            <TableRow style={{ backgroundColor: gantBg }}>
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  9. Imputación de subvenciones de inmovilizado no financiero y
                  otras
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = accData
                  .filter(
                    (a) =>
                      cuentasSubvencionesInmovilizado.includes(a.codigo) &&
                      a.mes === parseInt(mes),
                  )
                  .reduce((acc, a) => acc + (a.valor || 0), 0);

                const estimate = estimateData
                  .filter(
                    (e) =>
                      cuentasSubvencionesInmovilizado.includes(e.codigo) &&
                      e.mes === parseInt(mes),
                  )
                  .reduce((acc, e) => acc + (e.valor || 0), 0);

                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    cuentasSubvencionesInmovilizado.reduce((acc, codigo) => {
                      return (
                        acc +
                        accData
                          .filter((a) => a.codigo === codigo)
                          .reduce((sum, a) => sum + (a.valor || 0), 0)
                      );
                    }, 0),
                  )}
                >
                  {formatearMiles(
                    cuentasSubvencionesInmovilizado.reduce((acc, codigo) => {
                      return (
                        acc +
                        accData
                          .filter((a) => a.codigo === codigo)
                          .reduce((sum, a) => sum + (a.valor || 0), 0)
                      );
                    }, 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    cuentasSubvencionesInmovilizado.reduce((acc, codigo) => {
                      return (
                        acc +
                        estimateData
                          .filter((e) => e.codigo === codigo)
                          .reduce((sum, e) => sum + (e.valor || 0), 0)
                      );
                    }, 0),
                  )}
                >
                  {formatearMiles(
                    cuentasSubvencionesInmovilizado.reduce((acc, codigo) => {
                      return (
                        acc +
                        estimateData
                          .filter((e) => e.codigo === codigo)
                          .reduce((sum, e) => sum + (e.valor || 0), 0)
                      );
                    }, 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    cuentasSubvencionesInmovilizado.reduce((acc, codigo) => {
                      const real = accData
                        .filter((a) => a.codigo === codigo)
                        .reduce((sum, a) => sum + (a.valor || 0), 0);
                      const est = estimateData
                        .filter((e) => e.codigo === codigo)
                        .reduce((sum, e) => sum + (e.valor || 0), 0);
                      return acc + (real - est);
                    }, 0),
                  )}
                >
                  {formatearMiles(
                    cuentasSubvencionesInmovilizado.reduce((acc, codigo) => {
                      const real = accData
                        .filter((a) => a.codigo === codigo)
                        .reduce((sum, a) => sum + (a.valor || 0), 0);
                      const est = estimateData
                        .filter((e) => e.codigo === codigo)
                        .reduce((sum, e) => sum + (e.valor || 0), 0);
                      return acc + (real - est);
                    }, 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* 10. Excesos de provisiones */}
            <TableRow style={{ backgroundColor: gantBg }}>
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>10. Excesos de provisiones</strong>
              </TableCell>
              {meses.map((mes) => {
                const real = accData
                  .filter(
                    (a) => a.codigo === "79400000" && a.mes === parseInt(mes),
                  )
                  .reduce((acc, a) => acc + (a.valor || 0), 0);

                const estimate = estimateData
                  .filter(
                    (e) => e.codigo === "79400000" && e.mes === parseInt(mes),
                  )
                  .reduce((acc, e) => acc + (e.valor || 0), 0);

                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    accData
                      .filter((a) => a.codigo === "79400000")
                      .reduce((acc, a) => acc + (a.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    accData
                      .filter((a) => a.codigo === "79400000")
                      .reduce((acc, a) => acc + (a.valor || 0), 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    estimateData
                      .filter((e) => e.codigo === "79400000")
                      .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    estimateData
                      .filter((e) => e.codigo === "79400000")
                      .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    accData
                      .filter((a) => a.codigo === "79400000")
                      .reduce((acc, a) => acc + (a.valor || 0), 0) -
                      estimateData
                        .filter((e) => e.codigo === "79400000")
                        .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    accData
                      .filter((a) => a.codigo === "79400000")
                      .reduce((acc, a) => acc + (a.valor || 0), 0) -
                      estimateData
                        .filter((e) => e.codigo === "79400000")
                        .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* 11. Deterioro y resultado por enajenaciones del inmovilizado */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("enajenaciones")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["enajenaciones"] ? "▼" : "►"} 11. Deterioro y
                  resultado por enajenaciones del inmovilizado
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = accData
                  .filter(
                    (a) =>
                      [
                        ...cuentasEnajenacionesA,
                        ...cuentasEnajenacionesB,
                      ].includes(a.codigo) && a.mes === parseInt(mes),
                  )
                  .reduce((acc, a) => acc + (a.valor || 0), 0);

                const estimate = estimateData
                  .filter(
                    (e) =>
                      [
                        ...cuentasEnajenacionesA,
                        ...cuentasEnajenacionesB,
                      ].includes(e.codigo) && e.mes === parseInt(mes),
                  )
                  .reduce((acc, e) => acc + (e.valor || 0), 0);

                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    accData
                      .filter((a) =>
                        [
                          ...cuentasEnajenacionesA,
                          ...cuentasEnajenacionesB,
                        ].includes(a.codigo),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    accData
                      .filter((a) =>
                        [
                          ...cuentasEnajenacionesA,
                          ...cuentasEnajenacionesB,
                        ].includes(a.codigo),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    estimateData
                      .filter((e) =>
                        [
                          ...cuentasEnajenacionesA,
                          ...cuentasEnajenacionesB,
                        ].includes(e.codigo),
                      )
                      .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    estimateData
                      .filter((e) =>
                        [
                          ...cuentasEnajenacionesA,
                          ...cuentasEnajenacionesB,
                        ].includes(e.codigo),
                      )
                      .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    accData
                      .filter((a) =>
                        [
                          ...cuentasEnajenacionesA,
                          ...cuentasEnajenacionesB,
                        ].includes(a.codigo),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0) -
                      estimateData
                        .filter((e) =>
                          [
                            ...cuentasEnajenacionesA,
                            ...cuentasEnajenacionesB,
                          ].includes(e.codigo),
                        )
                        .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    accData
                      .filter((a) =>
                        [
                          ...cuentasEnajenacionesA,
                          ...cuentasEnajenacionesB,
                        ].includes(a.codigo),
                      )
                      .reduce((acc, a) => acc + (a.valor || 0), 0) -
                      estimateData
                        .filter((e) =>
                          [
                            ...cuentasEnajenacionesA,
                            ...cuentasEnajenacionesB,
                          ].includes(e.codigo),
                        )
                        .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                </span>
              </TableCell>
            </TableRow>
            {/* Hijas: solo si está abierto */}
            {gruposAbiertos["enajenaciones"] && (
              <>
                {/* a) Deterioros y pérdidas. */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>a) Deterioros y pérdidas</strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      accData.find(
                        (a) =>
                          a.codigo === "67000000" && a.mes === parseInt(mes),
                      )?.valor || 0;

                    const estimate =
                      estimateData.find(
                        (e) =>
                          e.codigo === "67000000" && e.mes === parseInt(mes),
                      )?.valor || 0;

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimateHead(
                        accData
                          .filter((a) => a.codigo === "67000000")
                          .reduce((acc, a) => acc + (a.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        accData
                          .filter((a) => a.codigo === "67000000")
                          .reduce((acc, a) => acc + (a.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        estimateData
                          .filter((e) => e.codigo === "67000000")
                          .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        estimateData
                          .filter((e) => e.codigo === "67000000")
                          .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        accData
                          .filter((a) => a.codigo === "67000000")
                          .reduce((acc, a) => acc + (a.valor || 0), 0) -
                          estimateData
                            .filter((e) => e.codigo === "67000000")
                            .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        accData
                          .filter((a) => a.codigo === "67000000")
                          .reduce((acc, a) => acc + (a.valor || 0), 0) -
                          estimateData
                            .filter((e) => e.codigo === "67000000")
                            .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* b) Resultados por enajenaciones y otras. */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>b) Resultados por enajenaciones y otras</strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      accData.find(
                        (a) =>
                          a.codigo === "77100000" && a.mes === parseInt(mes),
                      )?.valor || 0;

                    const estimate =
                      estimateData.find(
                        (e) =>
                          e.codigo === "77100000" && e.mes === parseInt(mes),
                      )?.valor || 0;

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimateHead(
                        accData
                          .filter((a) => a.codigo === "77100000")
                          .reduce((acc, a) => acc + (a.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        accData
                          .filter((a) => a.codigo === "77100000")
                          .reduce((acc, a) => acc + (a.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        estimateData
                          .filter((e) => e.codigo === "77100000")
                          .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        estimateData
                          .filter((e) => e.codigo === "77100000")
                          .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        accData
                          .filter((a) => a.codigo === "77100000")
                          .reduce((acc, a) => acc + (a.valor || 0), 0) -
                          estimateData
                            .filter((e) => e.codigo === "77100000")
                            .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    >
                      {formatearMiles(
                        accData
                          .filter((a) => a.codigo === "77100000")
                          .reduce((acc, a) => acc + (a.valor || 0), 0) -
                          estimateData
                            .filter((e) => e.codigo === "77100000")
                            .reduce((acc, e) => acc + (e.valor || 0), 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              </>
            )}

            {/* Diferencia negativa de combinaciones de negocio */}
            <TableRow style={{ backgroundColor: gantBg }}>
              <TableCell
                style={{
                  paddingLeft: "2em",
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong style={{ color: "grey" }}>
                  Diferencia negativa de combinaciones de negocio
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real =
                  accData.find(
                    (a) =>
                      a.codigo === "negative_comb_code" &&
                      a.mes === parseInt(mes),
                  )?.valor || 0;

                const estimate =
                  estimateData.find(
                    (e) =>
                      e.codigo === "negative_comb_code" &&
                      e.mes === parseInt(mes),
                  )?.valor || 0;

                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    accData
                      .filter((a) => a.codigo === "negative_comb_code")
                      .reduce((acc, a) => acc + (a.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    accData
                      .filter((a) => a.codigo === "negative_comb_code")
                      .reduce((acc, a) => acc + (a.valor || 0), 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    estimateData
                      .filter((e) => e.codigo === "negative_comb_code")
                      .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    estimateData
                      .filter((e) => e.codigo === "negative_comb_code")
                      .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    accData
                      .filter((a) => a.codigo === "negative_comb_code")
                      .reduce((acc, a) => acc + (a.valor || 0), 0) -
                      estimateData
                        .filter((e) => e.codigo === "negative_comb_code")
                        .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                >
                  {formatearMiles(
                    accData
                      .filter((a) => a.codigo === "negative_comb_code")
                      .reduce((acc, a) => acc + (a.valor || 0), 0) -
                      estimateData
                        .filter((e) => e.codigo === "negative_comb_code")
                        .reduce((acc, e) => acc + (e.valor || 0), 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* 12. Otros resultados */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("otrosResultados")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["otrosResultados"] ? "▼" : "►"} 12. Otros
                  resultados
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = accData
                  .filter(
                    (a) =>
                      cuentasOtrosResultados.includes(a.codigo) &&
                      a.mes === parseInt(mes),
                  )
                  .reduce((acc, a) => acc + (a.valor || 0), 0);

                const estimate = estimateData
                  .filter(
                    (e) =>
                      cuentasOtrosResultados.includes(e.codigo) &&
                      e.mes === parseInt(mes),
                  )
                  .reduce((acc, e) => acc + (e.valor || 0), 0);

                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}

              {/* Totales anuales */}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    cuentasOtrosResultados.reduce((acc, codigo) => {
                      return (
                        acc +
                        accData
                          .filter((a) => a.codigo === codigo)
                          .reduce((sum, a) => sum + (a.valor || 0), 0)
                      );
                    }, 0),
                  )}
                >
                  {formatearMiles(
                    cuentasOtrosResultados.reduce((acc, codigo) => {
                      return (
                        acc +
                        accData
                          .filter((a) => a.codigo === codigo)
                          .reduce((sum, a) => sum + (a.valor || 0), 0)
                      );
                    }, 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    cuentasOtrosResultados.reduce((acc, codigo) => {
                      return (
                        acc +
                        estimateData
                          .filter((e) => e.codigo === codigo)
                          .reduce((sum, e) => sum + (e.valor || 0), 0)
                      );
                    }, 0),
                  )}
                >
                  {formatearMiles(
                    cuentasOtrosResultados.reduce((acc, codigo) => {
                      return (
                        acc +
                        estimateData
                          .filter((e) => e.codigo === codigo)
                          .reduce((sum, e) => sum + (e.valor || 0), 0)
                      );
                    }, 0),
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    cuentasOtrosResultados.reduce((acc, codigo) => {
                      const real = accData
                        .filter((a) => a.codigo === codigo)
                        .reduce((sum, a) => sum + (a.valor || 0), 0);
                      const estimate = estimateData
                        .filter((e) => e.codigo === codigo)
                        .reduce((sum, e) => sum + (e.valor || 0), 0);
                      return acc + (real - estimate);
                    }, 0),
                  )}
                >
                  {formatearMiles(
                    cuentasOtrosResultados.reduce((acc, codigo) => {
                      const real = accData
                        .filter((a) => a.codigo === codigo)
                        .reduce((sum, a) => sum + (a.valor || 0), 0);
                      const estimate = estimateData
                        .filter((e) => e.codigo === codigo)
                        .reduce((sum, e) => sum + (e.valor || 0), 0);
                      return acc + (real - estimate);
                    }, 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* Hijas de 12. Otros resultados */}
            {gruposAbiertos["otrosResultados"] &&
              cuentasOtrosResultados.map((codigo) => {
                const lineaReal = accData.filter((a) => a.codigo === codigo);
                const lineaEst = estimateData.filter(
                  (e) => e.codigo === codigo,
                );
                const valoresMesReal = {};
                const valoresMesEst = {};
                let totalReal = 0;
                let totalEst = 0;

                meses.forEach((mes) => {
                  const r =
                    lineaReal.find((a) => a.mes === parseInt(mes))?.valor || 0;
                  const e =
                    lineaEst.find((e) => e.mes === parseInt(mes))?.valor || 0;
                  valoresMesReal[mes] = r;
                  valoresMesEst[mes] = e;
                  totalReal += r;
                  totalEst += e;
                });

                const titulo =
                  estructura.find((e) => e.codigo === codigo)?.titulo || codigo;

                return (
                  <TableRow key={codigo}>
                    <TableCell
                      style={{
                        paddingLeft: "2em",
                        position: "sticky",
                        left: 0,
                        backgroundColor: ` ${theme.palette.background.paper}`,
                      }}
                    >
                      {titulo}
                    </TableCell>
                    {meses.map((mes) => {
                      const diff = valoresMesReal[mes] - valoresMesEst[mes];
                      return (
                        <React.Fragment key={mes}>
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span
                              style={getEstiloValorEstimate(
                                valoresMesReal[mes],
                              )}
                            >
                              {formatearMiles(valoresMesReal[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(valoresMesEst[mes])}>
                              {formatearMiles(valoresMesEst[mes])}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(diff)}>
                              {formatearMiles(diff)}
                            </span>
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimate(totalReal)}>
                        {formatearMiles(totalReal)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor(totalEst)}>
                        {formatearMiles(totalEst)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor(totalReal - totalEst)}>
                        {formatearMiles(totalReal - totalEst)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}

            {/* A.1) RESULTADO DE EXPLOTACIÓN */}
            <TableRow style={{ backgroundColor: `${theme.palette.pyg1}` }}>
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: `${theme.palette.pyg1}`,
                }}
              >
                <strong>A.1) RESULTADO DE EXPLOTACIÓN</strong>
              </TableCell>
              {meses.map((mes) => {
                const real = resultadoA1Explotacion[mes] || 0;
                const est = estimateA1Explotacion[mes] || 0;
                const diff = real - est;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(est)}>
                        {formatearMiles(est)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    resultadoA1Explotacion.total,
                  )}
                >
                  {formatearMiles(resultadoA1Explotacion.total)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span style={getEstiloValor1(estimateA1Explotacion.total)}>
                  {formatearMiles(estimateA1Explotacion.total)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    resultadoA1Explotacion.total - estimateA1Explotacion.total,
                  )}
                >
                  {formatearMiles(
                    resultadoA1Explotacion.total - estimateA1Explotacion.total,
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* FALTA HACER EL ESTIMATE  */}
            {/* 14. Ingresos financieros */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("ingresosFinancieros")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["ingresosFinancieros"] ? "▼" : "►"} 14.
                  Ingresos financieros.
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = resultado14IngresosFinancieros[mes] || 0;
                const estimate =
                  calcularResumen(
                    "14. Ingresos financieros.",
                    estructura,
                    estimateData,
                  )[mes] || 0;
                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              {/* Totales */}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    resultado14IngresosFinancieros.total || 0,
                  )}
                >
                  {formatearMiles(resultado14IngresosFinancieros.total || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    calcularResumen(
                      "14. Ingresos financieros.",
                      estructura,
                      estimateData,
                    ).total || 0,
                  )}
                >
                  {formatearMiles(
                    calcularResumen(
                      "14. Ingresos financieros.",
                      estructura,
                      estimateData,
                    ).total || 0,
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    (resultado14IngresosFinancieros.total || 0) -
                      (calcularResumen(
                        "14. Ingresos financieros.",
                        estructura,
                        estimateData,
                      ).total || 0),
                  )}
                >
                  {formatearMiles(
                    (resultado14IngresosFinancieros.total || 0) -
                      (calcularResumen(
                        "14. Ingresos financieros.",
                        estructura,
                        estimateData,
                      ).total || 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {gruposAbiertos["ingresosFinancieros"] && (
              <>
                {/* a) De participaciones en instrumentos de patrimonio. */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>
                      a) De participaciones en instrumentos de patrimonio.
                    </strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "a) De participaciones en instrumentos de patrimonio.",
                        estructura,
                        accData,
                      )[mes] || 0;

                    const estimate =
                      calcularResumen(
                        "a) De participaciones en instrumentos de patrimonio.",
                        estructura,
                        estimateData,
                      )[mes] || 0;

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  {/* Totales */}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimateHead(
                        calcularResumen(
                          "a) De participaciones en instrumentos de patrimonio.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a) De participaciones en instrumentos de patrimonio.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        calcularResumen(
                          "a) De participaciones en instrumentos de patrimonio.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a) De participaciones en instrumentos de patrimonio.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        (calcularResumen(
                          "a) De participaciones en instrumentos de patrimonio.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "a) De participaciones en instrumentos de patrimonio.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    >
                      {formatearMiles(
                        (calcularResumen(
                          "a) De participaciones en instrumentos de patrimonio.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "a) De participaciones en instrumentos de patrimonio.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* a 1) En empresas del grupo y asociadas. */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "3em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    a 1) En empresas del grupo y asociadas.
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "a 1) En empresas del grupo y asociadas.",
                        estructura,
                        accData,
                      )[mes] || 0;

                    const estimate =
                      calcularResumen(
                        "a 1) En empresas del grupo y asociadas.",
                        estructura,
                        estimateData,
                      )[mes] || 0;

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  {/* Totales */}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "a 1) En empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a 1) En empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        calcularResumen(
                          "a 1) En empresas del grupo y asociadas.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a 1) En empresas del grupo y asociadas.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        (calcularResumen(
                          "a 1) En empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "a 1) En empresas del grupo y asociadas.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    >
                      {formatearMiles(
                        (calcularResumen(
                          "a 1) En empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "a 1) En empresas del grupo y asociadas.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* a 2) En terceros. */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "3em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    a 2) En terceros.
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen("a 2) En terceros.", estructura, accData)[
                        mes
                      ] || 0;

                    const estimate =
                      calcularResumen(
                        "a 2) En terceros.",
                        estructura,
                        estimateData,
                      )[mes] || 0;

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  {/* Totales */}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "a 2) En terceros.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a 2) En terceros.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        calcularResumen(
                          "a 2) En terceros.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a 2) En terceros.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        (calcularResumen(
                          "a 2) En terceros.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "a 2) En terceros.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    >
                      {formatearMiles(
                        (calcularResumen(
                          "a 2) En terceros.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "a 2) En terceros.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* b) De valores negociables y de créditos del activo inmovilizado. */}
                <TableRow
                  style={{
                    backgroundColor: ` ${theme.palette.estimates.main}`,
                  }}
                >
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.estimates.main}`,
                    }}
                  >
                    <strong>
                      b) De valores negociables y de créditos del activo
                      inmovilizado.
                    </strong>
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "b) De valores negociables y de créditos del activo inmovilizado.",
                        estructura,
                        accData,
                      )[mes] || 0;

                    const estimate =
                      calcularResumen(
                        "b) De valores negociables y de créditos del activo inmovilizado.",
                        estructura,
                        estimateData,
                      )[mes] || 0;

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimateHead(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor1(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimateHead(
                        calcularResumen(
                          "b) De valores negociables y de créditos del activo inmovilizado.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "b) De valores negociables y de créditos del activo inmovilizado.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        calcularResumen(
                          "b) De valores negociables y de créditos del activo inmovilizado.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "b) De valores negociables y de créditos del activo inmovilizado.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor1(
                        (calcularResumen(
                          "b) De valores negociables y de créditos del activo inmovilizado.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "b) De valores negociables y de créditos del activo inmovilizado.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    >
                      {formatearMiles(
                        (calcularResumen(
                          "b) De valores negociables y de créditos del activo inmovilizado.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "b) De valores negociables y de créditos del activo inmovilizado.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* b 1) De empresas del grupo y asociadas. */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "3em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    b 1) De empresas del grupo y asociadas.
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "b 1) De empresas del grupo y asociadas.",
                        estructura,
                        accData,
                      )[mes] || 0;

                    const estimate =
                      calcularResumen(
                        "b 1) De empresas del grupo y asociadas.",
                        estructura,
                        estimateData,
                      )[mes] || 0;

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "b 1) De empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "b 1) De empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        calcularResumen(
                          "b 1) De empresas del grupo y asociadas.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "b 1) De empresas del grupo y asociadas.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        (calcularResumen(
                          "b 1) De empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "b 1) De empresas del grupo y asociadas.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    >
                      {formatearMiles(
                        (calcularResumen(
                          "b 1) De empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "b 1) De empresas del grupo y asociadas.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* b 2) De terceros. */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "3em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    b 2) De terceros.
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen("b 2) De terceros.", estructura, accData)[
                        mes
                      ] || 0;

                    const estimate =
                      calcularResumen(
                        "b 2) De terceros.",
                        estructura,
                        estimateData,
                      )[mes] || 0;

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "b 2) De terceros.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "b 2) De terceros.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        calcularResumen(
                          "b 2) De terceros.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "b 2) De terceros.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        (calcularResumen(
                          "b 2) De terceros.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "b 2) De terceros.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    >
                      {formatearMiles(
                        (calcularResumen(
                          "b 2) De terceros.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "b 2) De terceros.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* c) Imputación de subvenciones, donaciones y legados de carácter financiero */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    c) Imputación de subvenciones, donaciones y legados de
                    carácter financiero
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "c) Imputación de subvenciones, donaciones y legados de carácter financiero.",
                        estructura,
                        accData,
                      )[mes] || 0;

                    const estimate =
                      calcularResumen(
                        "c) Imputación de subvenciones, donaciones y legados de carácter financiero.",
                        estructura,
                        estimateData,
                      )[mes] || 0;

                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "c) Imputación de subvenciones, donaciones y legados de carácter financiero.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "c) Imputación de subvenciones, donaciones y legados de carácter financiero.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        calcularResumen(
                          "c) Imputación de subvenciones, donaciones y legados de carácter financiero.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "c) Imputación de subvenciones, donaciones y legados de carácter financiero.",
                          estructura,
                          estimateData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        (calcularResumen(
                          "c) Imputación de subvenciones, donaciones y legados de carácter financiero.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "c) Imputación de subvenciones, donaciones y legados de carácter financiero.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    >
                      {formatearMiles(
                        (calcularResumen(
                          "c) Imputación de subvenciones, donaciones y legados de carácter financiero.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (calcularResumen(
                            "c) Imputación de subvenciones, donaciones y legados de carácter financiero.",
                            estructura,
                            estimateData,
                          ).total || 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              </>
            )}
            {/* FALTA HACER EL ESTIMATE  */}

            {/* 15. Gastos financieros */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("gastosFinancieros")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["gastosFinancieros"] ? "▼" : "►"} 15. Gastos
                  financieros
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = resultado15GastosFinancieros[mes] || 0;
                const estimate =
                  (estimateDeudasGrupo[mes] || 0) +
                  (estimateDeudasTerceros[mes] || 0) +
                  (estimateActualizacionProvisiones[mes] || 0);
                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    resultado15GastosFinancieros.total || 0,
                  )}
                >
                  {formatearMiles(resultado15GastosFinancieros.total || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    estimateDeudasGrupo.total +
                      estimateDeudasTerceros.total +
                      estimateActualizacionProvisiones.total,
                  )}
                >
                  {formatearMiles(
                    estimateDeudasGrupo.total +
                      estimateDeudasTerceros.total +
                      estimateActualizacionProvisiones.total,
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    (resultado15GastosFinancieros.total || 0) -
                      (estimateDeudasGrupo.total +
                        estimateDeudasTerceros.total +
                        estimateActualizacionProvisiones.total),
                  )}
                >
                  {formatearMiles(
                    (resultado15GastosFinancieros.total || 0) -
                      (estimateDeudasGrupo.total +
                        estimateDeudasTerceros.total +
                        estimateActualizacionProvisiones.total),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {gruposAbiertos["gastosFinancieros"] && (
              <>
                {/* a) Por deudas con empresas del grupo y asociadas. */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    a) Por deudas con empresas del grupo y asociadas.
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "a) Por deudas con empresas del grupo y asociadas.",
                        estructura,
                        accData,
                      )[mes] || 0;
                    const estimate = estimateDeudasGrupo[mes] || 0;
                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "a) Por deudas con empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a) Por deudas con empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(estimateDeudasGrupo.total || 0)}
                    >
                      {formatearMiles(estimateDeudasGrupo.total || 0)}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        (calcularResumen(
                          "a) Por deudas con empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0) - (estimateDeudasGrupo.total || 0),
                      )}
                    >
                      {formatearMiles(
                        (calcularResumen(
                          "a) Por deudas con empresas del grupo y asociadas.",
                          estructura,
                          accData,
                        ).total || 0) - (estimateDeudasGrupo.total || 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* b) Por deudas con terceros. */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    b) Por deudas con terceros.
                  </TableCell>
                  {meses.map((mes) => {
                    const real = resultadoDeudasTerceros[mes] || 0;
                    const estimate = estimateDeudasTerceros[mes] || 0;
                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        resultadoDeudasTerceros.total || 0,
                      )}
                    >
                      {formatearMiles(resultadoDeudasTerceros.total || 0)}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(estimateDeudasTerceros.total || 0)}
                    >
                      {formatearMiles(estimateDeudasTerceros.total || 0)}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        (resultadoDeudasTerceros.total || 0) -
                          (estimateDeudasTerceros.total || 0),
                      )}
                    >
                      {formatearMiles(
                        (resultadoDeudasTerceros.total || 0) -
                          (estimateDeudasTerceros.total || 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* c) Por actualización de provisiones. */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    c) Por actualización de provisiones.
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "c) Por actualización de provisiones.",
                        estructura,
                        accData,
                      )[mes] || 0;
                    const estimate = estimateActualizacionProvisiones[mes] || 0;
                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "c) Por actualización de provisiones.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "c) Por actualización de provisiones.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        estimateActualizacionProvisiones.total || 0,
                      )}
                    >
                      {formatearMiles(
                        estimateActualizacionProvisiones.total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        (calcularResumen(
                          "c) Por actualización de provisiones.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (estimateActualizacionProvisiones.total || 0),
                      )}
                    >
                      {formatearMiles(
                        (calcularResumen(
                          "c) Por actualización de provisiones.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (estimateActualizacionProvisiones.total || 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              </>
            )}

            {/* 16. Variación del valor razonable en instrumentos financieros */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("valorRazonable")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["valorRazonable"] ? "▼" : "►"} 16. Variación
                  del valor razonable en instrumentos financieros.
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real =
                  calcularResumen(
                    "16. Variación del valor razonable en instrumentos financieros.",
                    estructura,
                    accData,
                  )[mes] || 0;
                const estimate = estimateValorRazonable[mes] || 0;
                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    calcularResumen(
                      "16. Variación del valor razonable en instrumentos financieros.",
                      estructura,
                      accData,
                    ).total || 0,
                  )}
                >
                  {formatearMiles(
                    calcularResumen(
                      "16. Variación del valor razonable en instrumentos financieros.",
                      estructura,
                      accData,
                    ).total || 0,
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(estimateValorRazonable.total || 0)}
                >
                  {formatearMiles(estimateValorRazonable.total || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    (calcularResumen(
                      "16. Variación del valor razonable en instrumentos financieros.",
                      estructura,
                      accData,
                    ).total || 0) - (estimateValorRazonable.total || 0),
                  )}
                >
                  {formatearMiles(
                    (calcularResumen(
                      "16. Variación del valor razonable en instrumentos financieros.",
                      estructura,
                      accData,
                    ).total || 0) - (estimateValorRazonable.total || 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {gruposAbiertos["valorRazonable"] && (
              <>
                {/* a) Cartera de negociación y otros. */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    a) Cartera de negociación y otros.
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "a) Cartera de negociación y otros.",
                        estructura,
                        accData,
                      )[mes] || 0;
                    const estimate =
                      getEstimateMeses(["76900000", "66900000"])[mes] || 0;
                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "a) Cartera de negociación y otros.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a) Cartera de negociación y otros.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        getEstimateMeses(["76900000", "66900000"]).total || 0,
                      )}
                    >
                      {formatearMiles(
                        getEstimateMeses(["76900000", "66900000"]).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        (calcularResumen(
                          "a) Cartera de negociación y otros.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (getEstimateMeses(["76900000", "66900000"]).total ||
                            0),
                      )}
                    >
                      {formatearMiles(
                        (calcularResumen(
                          "a) Cartera de negociación y otros.",
                          estructura,
                          accData,
                        ).total || 0) -
                          (getEstimateMeses(["76900000", "66900000"]).total ||
                            0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* b) Imputación al resultado por activos financieros disponibles para la venta */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    b) Imputación al resultado por activos financieros
                    disponibles para la venta
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "b) Imputación al resultado por activos financieros disponibles para la venta",
                        estructura,
                        accData,
                      )[mes] || 0;
                    const estimate = getEstimateMeses(["76600000"])[mes] || 0;
                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "b) Imputación al resultado por activos financieros disponibles para la venta",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "b) Imputación al resultado por activos financieros disponibles para la venta",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        getEstimateMeses(["76600000"]).total || 0,
                      )}
                    >
                      {formatearMiles(
                        getEstimateMeses(["76600000"]).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        (calcularResumen(
                          "b) Imputación al resultado por activos financieros disponibles para la venta",
                          estructura,
                          accData,
                        ).total || 0) -
                          (getEstimateMeses(["76600000"]).total || 0),
                      )}
                    >
                      {formatearMiles(
                        (calcularResumen(
                          "b) Imputación al resultado por activos financieros disponibles para la venta",
                          estructura,
                          accData,
                        ).total || 0) -
                          (getEstimateMeses(["76600000"]).total || 0),
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              </>
            )}

            {/* 17. Diferencias de cambio */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("diferenciasCambio")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["diferenciasCambio"] ? "▼" : "►"} 17.
                  Diferencias de cambio
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real = resultadoDiferenciasCambio[mes] || 0;
                const estimate = estimateDiferenciasCambio[mes] || 0;
                const diff = real - estimate;
                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    resultadoDiferenciasCambio.total || 0,
                  )}
                >
                  {formatearMiles(resultadoDiferenciasCambio.total || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(estimateDiferenciasCambio.total || 0)}
                >
                  {formatearMiles(estimateDiferenciasCambio.total || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    (resultadoDiferenciasCambio.total || 0) -
                      (estimateDiferenciasCambio.total || 0),
                  )}
                >
                  {formatearMiles(
                    (resultadoDiferenciasCambio.total || 0) -
                      (estimateDiferenciasCambio.total || 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {gruposAbiertos["diferenciasCambio"] &&
              cuentasDiferenciasCambio.map((codigo) => {
                const linea = accData.filter((a) => a.codigo === codigo);
                const valoresMes = {};
                let total = 0;

                meses.forEach((mes) => {
                  const valor =
                    linea.find((a) => a.mes === parseInt(mes))?.valor || 0;
                  valoresMes[mes] = valor;
                  total += valor;
                });

                const estimateLinea = estimateData.filter(
                  (e) => e.codigo === codigo,
                );
                const estimateValoresMes = {};
                let estimateTotal = 0;

                meses.forEach((mes) => {
                  const valor =
                    estimateLinea.find((a) => a.mes === parseInt(mes))?.valor ||
                    0;
                  estimateValoresMes[mes] = valor;
                  estimateTotal += valor;
                });

                const titulo =
                  estructura.find((e) => e.codigo === codigo)?.titulo || codigo;

                return (
                  <TableRow key={codigo}>
                    <TableCell
                      style={{
                        paddingLeft: "2em",
                        position: "sticky",
                        left: 0,
                        backgroundColor: ` ${theme.palette.background.paper}`,
                      }}
                    >
                      {titulo}
                    </TableCell>
                    {meses.map((mes) => {
                      const real = valoresMes[mes];
                      const estimate = estimateValoresMes[mes];
                      const diff = real - estimate;
                      return (
                        <React.Fragment key={mes}>
                          <TableCell
                            align="right"
                            sx={{
                              borderLeft: `1px solid ${theme.palette.pyg}`,
                            }}
                          >
                            <span style={getEstiloValorEstimate(real)}>
                              {formatearMiles(real)}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(estimate)}>
                              {formatearMiles(estimate)}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            <span style={getEstiloValor(diff)}>
                              {formatearMiles(diff)}
                            </span>
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimate(total)}>
                        {formatearMiles(total)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor(estimateTotal)}>
                        {formatearMiles(estimateTotal)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor(total - estimateTotal)}>
                        {formatearMiles(total - estimateTotal)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}

            {/* 18. Deterioro y resultado por enajenaciones de instrumentos financieros */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("enajenaciones")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["enajenaciones"] ? "▼" : "►"} 18. Deterioro y
                  resultado por enajenaciones de instrumentos financieros
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real =
                  calcularResumen(
                    "18. Deterioro y resultado por enajenaciones de instrumentos financieros.",
                    estructura,
                    accData,
                  )[mes] || 0;

                const estimate = 0; // Puedes ajustar si tienes cuentas en el futuro
                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    calcularResumen(
                      "18. Deterioro y resultado por enajenaciones de instrumentos financieros.",
                      estructura,
                      accData,
                    ).total || 0,
                  )}
                >
                  {formatearMiles(
                    calcularResumen(
                      "18. Deterioro y resultado por enajenaciones de instrumentos financieros.",
                      estructura,
                      accData,
                    ).total || 0,
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span style={getEstiloValor1(0)}>{formatearMiles(0)}</span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    calcularResumen(
                      "18. Deterioro y resultado por enajenaciones de instrumentos financieros.",
                      estructura,
                      accData,
                    ).total || 0,
                  )}
                >
                  {formatearMiles(
                    calcularResumen(
                      "18. Deterioro y resultado por enajenaciones de instrumentos financieros.",
                      estructura,
                      accData,
                    ).total || 0,
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* Subgrupos de 18 */}
            {gruposAbiertos["enajenaciones"] && (
              <>
                {/* a) Deterioros y pérdidas */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    a) Deterioros y pérdidas
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "a) Deterioros y pérdidas.",
                        estructura,
                        accData,
                      )[mes] || 0;

                    const estimate = 0;
                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "a) Deterioros y pérdidas.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a) Deterioros y pérdidas.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={getEstiloValor(0)}>{formatearMiles(0)}</span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        calcularResumen(
                          "a) Deterioros y pérdidas.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a) Deterioros y pérdidas.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* b) Resultados por enajenaciones y otras */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    b) Resultados por enajenaciones y otras
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "b) Resultados por enajenaciones y otras.",
                        estructura,
                        accData,
                      )[mes] || 0;

                    const estimate = 0;
                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "b) Resultados por enajenaciones y otras.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "b) Resultados por enajenaciones y otras.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={getEstiloValor(0)}>{formatearMiles(0)}</span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        calcularResumen(
                          "b) Resultados por enajenaciones y otras.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "b) Resultados por enajenaciones y otras.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              </>
            )}

            {/* 19. Otros ingresos y gastos de carácter financiero */}
            <TableRow
              style={{
                backgroundColor: gantBg,
                cursor: "pointer",
              }}
              onClick={() => toggleGrupo("otrosIngresosFinancieros")}
            >
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: gantBg,
                }}
              >
                <strong>
                  {gruposAbiertos["otrosIngresosFinancieros"] ? "▼" : "►"} 19.
                  Otros ingresos y gastos de carácter financiero
                </strong>
              </TableCell>
              {meses.map((mes) => {
                const real =
                  calcularResumen(
                    "19. Otros ingresos y gastos de carácter financiero",
                    estructura,
                    accData,
                  )[mes] || 0;

                const estimate = 0;
                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    calcularResumen(
                      "19. Otros ingresos y gastos de carácter financiero",
                      estructura,
                      accData,
                    ).total || 0,
                  )}
                >
                  {formatearMiles(
                    calcularResumen(
                      "19. Otros ingresos y gastos de carácter financiero",
                      estructura,
                      accData,
                    ).total || 0,
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span style={getEstiloValor1(0)}>{formatearMiles(0)}</span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    calcularResumen(
                      "19. Otros ingresos y gastos de carácter financiero",
                      estructura,
                      accData,
                    ).total || 0,
                  )}
                >
                  {formatearMiles(
                    calcularResumen(
                      "19. Otros ingresos y gastos de carácter financiero",
                      estructura,
                      accData,
                    ).total || 0,
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* Subgrupos de 19 */}
            {gruposAbiertos["otrosIngresosFinancieros"] && (
              <>
                {/* a) Incorporación al activo de gastos financieros */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    a) Incorporación al activo de gastos financieros
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "a) Incorporación al activo de gastos financieros",
                        estructura,
                        accData,
                      )[mes] || 0;
                    const estimate = 0;
                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "a) Incorporación al activo de gastos financieros",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a) Incorporación al activo de gastos financieros",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={getEstiloValor(0)}>{formatearMiles(0)}</span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        calcularResumen(
                          "a) Incorporación al activo de gastos financieros",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "a) Incorporación al activo de gastos financieros",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* b) Ingresos financieros derivados de convenios de acreedores */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    b) Ingresos financieros derivados de convenios de
                    acreedores.
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "b) Ingresos financieros derivados de convenios de acreedores.",
                        estructura,
                        accData,
                      )[mes] || 0;
                    const estimate = 0;
                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "b) Ingresos financieros derivados de convenios de acreedores.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "b) Ingresos financieros derivados de convenios de acreedores.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={getEstiloValor(0)}>{formatearMiles(0)}</span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        calcularResumen(
                          "b) Ingresos financieros derivados de convenios de acreedores.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "b) Ingresos financieros derivados de convenios de acreedores.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                </TableRow>

                {/* c) Resto de ingresos y gastos */}
                <TableRow>
                  <TableCell
                    style={{
                      paddingLeft: "2em",
                      position: "sticky",
                      left: 0,
                      backgroundColor: ` ${theme.palette.background.paper}`,
                    }}
                  >
                    c) Resto de ingresos y gastos.
                  </TableCell>
                  {meses.map((mes) => {
                    const real =
                      calcularResumen(
                        "c) Resto de ingresos y gastos.",
                        estructura,
                        accData,
                      )[mes] || 0;
                    const estimate = 0;
                    const diff = real - estimate;

                    return (
                      <React.Fragment key={mes}>
                        <TableCell
                          align="right"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg}`,
                          }}
                        >
                          <span style={getEstiloValorEstimate(real)}>
                            {formatearMiles(real)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(estimate)}>
                            {formatearMiles(estimate)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span style={getEstiloValor(diff)}>
                            {formatearMiles(diff)}
                          </span>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.pyg}`,
                    }}
                  >
                    <span
                      style={getEstiloValorEstimate(
                        calcularResumen(
                          "c) Resto de ingresos y gastos.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "c) Resto de ingresos y gastos.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={getEstiloValor(0)}>{formatearMiles(0)}</span>
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={getEstiloValor(
                        calcularResumen(
                          "c) Resto de ingresos y gastos.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    >
                      {formatearMiles(
                        calcularResumen(
                          "c) Resto de ingresos y gastos.",
                          estructura,
                          accData,
                        ).total || 0,
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              </>
            )}

            {/* A.2) RESULTADO FINANCIERO */}
            <TableRow style={{ backgroundColor: `${theme.palette.pyg1}` }}>
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: `${theme.palette.pyg1}`,
                }}
              >
                <strong>A.2) RESULTADO FINANCIERO</strong>
              </TableCell>
              {meses.map((mes) => {
                const real = resultadoA2Financiero[mes] || 0;
                const ingresosFinancieros =
                  getEstimateMeses([
                    "76100000",
                    "76110000",
                    "76200000",
                    "76300000",
                    "76500000",
                  ])[mes] || 0;
                const gastosFinancieros =
                  getEstimateMeses([
                    ...cuentasDeudasGrupo,
                    ...cuentasDeudasTerceros,
                    ...cuentasActualizacionProvisiones,
                  ])[mes] || 0;
                const variacionValorRazonable =
                  getEstimateMeses(["76600000", "76900000", "66900000"])[mes] ||
                  0;
                const diferenciasCambio =
                  getEstimateMeses(cuentasDiferenciasCambio)[mes] || 0;

                const estimate =
                  ingresosFinancieros +
                  gastosFinancieros +
                  variacionValorRazonable +
                  diferenciasCambio;

                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              {/* Totales */}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    resultadoA2Financiero.total || 0,
                  )}
                >
                  {formatearMiles(resultadoA2Financiero.total || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    getEstimateMeses([
                      "76100000",
                      "76110000",
                      "76200000",
                      "76300000",
                      "76500000",
                      ...cuentasDeudasGrupo,
                      ...cuentasDeudasTerceros,
                      ...cuentasActualizacionProvisiones,
                      "76600000",
                      "76900000",
                      "66900000",
                      ...cuentasDiferenciasCambio,
                    ]).total || 0,
                  )}
                >
                  {formatearMiles(
                    getEstimateMeses([
                      "76100000",
                      "76110000",
                      "76200000",
                      "76300000",
                      "76500000",
                      ...cuentasDeudasGrupo,
                      ...cuentasDeudasTerceros,
                      ...cuentasActualizacionProvisiones,
                      "76600000",
                      "76900000",
                      "66900000",
                      ...cuentasDiferenciasCambio,
                    ]).total || 0,
                  )}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    (resultadoA2Financiero.total || 0) -
                      (getEstimateMeses([
                        "76100000",
                        "76110000",
                        "76200000",
                        "76300000",
                        "76500000",
                        ...cuentasDeudasGrupo,
                        ...cuentasDeudasTerceros,
                        ...cuentasActualizacionProvisiones,
                        "76600000",
                        "76900000",
                        "66900000",
                        ...cuentasDiferenciasCambio,
                      ]).total || 0),
                  )}
                >
                  {formatearMiles(
                    (resultadoA2Financiero.total || 0) -
                      (getEstimateMeses([
                        "76100000",
                        "76110000",
                        "76200000",
                        "76300000",
                        "76500000",
                        ...cuentasDeudasGrupo,
                        ...cuentasDeudasTerceros,
                        ...cuentasActualizacionProvisiones,
                        "76600000",
                        "76900000",
                        "66900000",
                        ...cuentasDiferenciasCambio,
                      ]).total || 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            {/* C) RESULTADO ANTES DE IMPUESTOS */}
            <TableRow style={{ backgroundColor: `${theme.palette.cfverde}` }}>
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: `${theme.palette.cfverde}`,
                }}
              >
                <strong>C) RESULTADO ANTES DE IMPUESTOS</strong>
              </TableCell>
              {meses.map((mes) => {
                const real = resultadoAntesImpuestos[mes] || 0;
                const estimate = estimateAntesImpuestos[mes] || 0;
                const diff = real - estimate;

                return (
                  <React.Fragment key={mes}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(
                    resultadoAntesImpuestos.total || 0,
                  )}
                >
                  {formatearMiles(resultadoAntesImpuestos.total || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(estimateAntesImpuestos.total || 0)}
                >
                  {formatearMiles(estimateAntesImpuestos.total || 0)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span
                  style={getEstiloValor1(
                    (resultadoAntesImpuestos.total || 0) -
                      (estimateAntesImpuestos.total || 0),
                  )}
                >
                  {formatearMiles(
                    (resultadoAntesImpuestos.total || 0) -
                      (estimateAntesImpuestos.total || 0),
                  )}
                </span>
              </TableCell>
            </TableRow>

            <PygPipelineSerieDebugRow
              titulo="PROYECCIÓN VENTAS"
              resultado={resultadoProyeccionVentas}
              meses={meses}
              theme={theme}
              formatearMiles={formatearMiles}
              getEstiloValorEstimateHead={getEstiloValorEstimateHead}
              getEstiloValor1={getEstiloValor1}
              onOpenDetalle={openSerieDetalle}
              rowKeyPrefix="proy-ventas"
            />

            <PygPipelineSerieDebugRow
              titulo="PROYECCIÓN COMPRAS"
              resultado={resultadoProyeccionCompras}
              meses={meses}
              theme={theme}
              formatearMiles={formatearMiles}
              getEstiloValorEstimateHead={getEstiloValorEstimateHead}
              getEstiloValor1={getEstiloValor1}
              onOpenDetalle={openSerieDetalle}
              rowKeyPrefix="proy-compras"
            />

            <PygPipelineSerieDebugRow
              titulo="PEDIDOS DE VENTA"
              resultado={resultadoPedidos_Ventas}
              meses={meses}
              theme={theme}
              formatearMiles={formatearMiles}
              getEstiloValorEstimateHead={getEstiloValorEstimateHead}
              getEstiloValor1={getEstiloValor1}
              onOpenDetalle={openSerieDetalle}
              rowKeyPrefix="ped-venta"
            />

            <PygPipelineSerieDebugRow
              titulo="FACTURAS BORRADOR VENTA"
              resultado={resultadoFacturasBorrador_Ventas}
              meses={meses}
              theme={theme}
              formatearMiles={formatearMiles}
              getEstiloValorEstimateHead={getEstiloValorEstimateHead}
              getEstiloValor1={getEstiloValor1}
              onOpenDetalle={openSerieDetalle}
              rowKeyPrefix="fac-borrador"
            />

            <PygPipelineSerieDebugRow
              titulo="PEDIDOS DE COMPRA"
              resultado={resultadoPedidos_Compras}
              meses={meses}
              theme={theme}
              formatearMiles={formatearMiles}
              getEstiloValorEstimateHead={getEstiloValorEstimateHead}
              getEstiloValor1={getEstiloValor1}
              onOpenDetalle={openSerieDetalle}
              rowKeyPrefix="ped-compra"
            />

            {/*  */}
            {/* RESULTADO FINAL */}
            <TableRow style={{ backgroundColor: `${theme.palette.pyg2}` }}>
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: `${theme.palette.pyg2}`,
                }}
              >
                <strong>RESULTADO FINAL</strong>
              </TableCell>
              {meses.map((mes) => {
                const { real, estimate, diff } = resultadoFinal[mes] || {
                  real: 0,
                  estimate: 0,
                  diff: 0,
                };

                return (
                  <React.Fragment key={`resultado-final-${mes}`}>
                    <TableCell
                      align="right"
                      sx={{
                        borderLeft: `1px solid ${theme.palette.pyg}`,
                      }}
                    >
                      <span style={getEstiloValorEstimateHead(real)}>
                        {formatearMiles(real)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(estimate)}>
                        {formatearMiles(estimate)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={getEstiloValor1(diff)}>
                        {formatearMiles(diff)}
                      </span>
                    </TableCell>
                  </React.Fragment>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `1px solid ${theme.palette.pyg}` }}
              >
                <span
                  style={getEstiloValorEstimateHead(resultadoFinal.total.real)}
                >
                  {formatearMiles(resultadoFinal.total.real)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span style={getEstiloValor1(resultadoFinal.total.estimate)}>
                  {formatearMiles(resultadoFinal.total.estimate)}
                </span>
              </TableCell>
              <TableCell align="right">
                <span style={getEstiloValor1(resultadoFinal.total.diff)}>
                  {formatearMiles(resultadoFinal.total.diff)}
                </span>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <PygSerieDetalleDialog
        open={Boolean(detalleDialog)}
        onClose={closeSerieDetalle}
        titulo={detalleDialog?.titulo}
        year={selectedYear}
        empresa={empresa}
        mes={detalleDialog?.mes ?? null}
        vistaLabel={titulo}
        totalEsperado={detalleDialog?.totalEsperado ?? null}
      />
    </Box>
  );
};

export default PygDebugTable;
